/* project-core.js — data layer for trade projects / shopping lists.
 * Theme-agnostic, DOM-free: document model (v3), storage adapters and the
 * mutation operations. Consumed by project-ui.js (or any other renderer).
 *
 * Doc shape (v3): { v:3, _rev?, activeProjectId, projects:[{ id, name,
 *   projectNumber, customerRef, notes, status, createdAt,
 *   activeOrderId, orderSeq, orders:[{ id, seq, label, poNumber,
 *   quoteNumber, readyBy, status, createdAt,
 *   groups:[{ id, label, position, collapsed }],
 *   lines:[{ id, handle, variantId, title, image, price, qty, note,
 *            groupId, addedAt }] }] }] }
 *
 * A project is the customer job/site; ORDERS split it into individually
 * priceable stages that each carry their own PO # / quote # and eventually
 * check out one-to-one as Shopify orders. seq is per-project, monotonic,
 * never reused — archived orders keep theirs (reporting spine).
 *
 * GROUPS (v3, RFC §3.2) are room/area dividers INSIDE an order — "Ensuite",
 * "Master bathroom", "Kitchen", "Laundry", "Outdoor shower" — so the trade
 * customer can say what goes where. A line's groupId names its room; null is
 * the "Unassigned" pseudo-group, which is never stored. position is the drag
 * order (1000-step, gaps allowed).
 *
 * Storage: LocalAdapter (localStorage) is always the fast working store and,
 * for guests, the pre-login BUFFER (RFC §4: the server never stores anything
 * for an anonymous visitor). ProxyAdapter wraps it for signed-in customers:
 * same read()/save(doc) surface, plus sync to the projects app through the
 * App Proxy (/apps/projects/api) with `_rev` compare-and-set, a one-time
 * buffer merge on the first authenticated boot, and a graceful fall back to
 * the buffer when the proxy is unavailable.
 */
(function (global) {
  'use strict';

  const uid = (p) => p + '_' + Math.random().toString(36).slice(2, 9);
  const now = () => new Date().toISOString();
  const DOC_VERSION = 3;
  const POSITION_STEP = 1000;
  /** Placeholder examples for a room label (RFC §7); rotated per new group */
  const ROOM_EXAMPLES = ['Ensuite', 'Master bathroom', 'Kitchen', 'Laundry', 'Outdoor shower'];

  function blankOrder(seq) {
    return {
      id: uid('o'), seq: seq, label: '',
      poNumber: '', quoteNumber: '',
      /* READY BY (Luke, 2026-09-17): the date this order needs to be on site,
       * entered by the customer or the trade user for their customer. It is
       * the shopper's planning date and Sydney Taps' demand signal; it rides
       * onto every cart line of the order as the "Ready by" property. ISO
       * yyyy-mm-dd or '' — never a Date object (the doc is JSON). */
      readyBy: '',
      status: 'open', createdAt: now(), groups: [], lines: [],
    };
  }
  function blankProject(id, name) {
    const o = blankOrder(1);
    return {
      id: id, name: name,
      projectNumber: '', customerRef: '', notes: '',
      status: 'open', createdAt: now(),
      activeOrderId: o.id, orderSeq: 1, orders: [o],
    };
  }
  function blankDoc() {
    const pid = uid('p');
    return { v: DOC_VERSION, activeProjectId: pid, projects: [blankProject(pid, 'My project')] };
  }

  /** v1 → v2: each project's lists become orders (seq in list order),
   *  inheriting the project-level PO / quote onto the first order */
  function migrateV1(doc) {
    doc.projects.forEach((p) => {
      const lists = p.lists && p.lists.length ? p.lists : [{ id: 'general', name: 'General', lines: [] }];
      p.orders = lists.map((l, i) => ({
        id: uid('o'), seq: i + 1,
        label: l.name === 'General' ? '' : (l.name || ''),
        poNumber: i === 0 ? (p.poNumber || '') : '',
        quoteNumber: i === 0 ? (p.quoteNumber || '') : '',
        status: 'open', createdAt: p.createdAt || now(),
        lines: l.lines || [],
      }));
      p.orderSeq = p.orders.length;
      p.activeOrderId = p.orders[0].id;
      delete p.lists; delete p.poNumber; delete p.quoteNumber;
    });
    doc.v = 2;
    return doc;
  }
  /** v2 → v3: orders gain groups, lines gain ids + groupId. Idempotent. */
  function migrateV2(doc) {
    doc.projects.forEach((p) => {
      (p.orders || []).forEach((o) => {
        if (!Array.isArray(o.groups)) o.groups = [];
        (o.lines || []).forEach((l) => {
          if (!l.id) l.id = uid('l');
          if (l.groupId === undefined) l.groupId = null;
        });
      });
    });
    doc.v = DOC_VERSION;
    return doc;
  }
  /** fields added within v3 without a version bump (readyBy): a stored doc
   *  from before the field simply gains the default. Idempotent. */
  function normaliseV3(doc) {
    (doc.projects || []).forEach((p) => {
      (p.orders || []).forEach((o) => { if (typeof o.readyBy !== 'string') o.readyBy = ''; });
    });
    return doc;
  }
  /** any supported version → current */
  function migrate(doc) {
    if (doc.v === 1) migrateV1(doc);
    if (doc.v === 2) migrateV2(doc);
    return doc.v === DOC_VERSION ? normaliseV3(doc) : null;
  }

  function LocalAdapter(opts) {
    opts = opts || {};
    const KEY = opts.key || 'st:projects:v1';
    const LEGACY = opts.legacyKeys || ['st_project', 'st_wishlist'];
    function migrateLegacy() {
      const doc = blankDoc();
      try {
        const seen = new Set();
        LEGACY.forEach((k) => {
          JSON.parse(localStorage.getItem(k) || '[]').forEach((x) => {
            if (x && x.handle && !seen.has(x.handle)) {
              seen.add(x.handle);
              doc.projects[0].orders[0].lines.push({
                id: uid('l'), handle: x.handle, variantId: x.variantId || null,
                title: x.title || x.handle, image: x.image || '',
                price: null, qty: 1, note: '', groupId: null, addedAt: now(),
              });
            }
          });
        });
      } catch (e) { /* ignore corrupt legacy data */ }
      LEGACY.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(KEY, JSON.stringify(doc));
      return doc;
    }
    return {
      key: KEY,
      read() {
        try {
          const d = JSON.parse(localStorage.getItem(KEY));
          if (d && Array.isArray(d.projects) && d.projects.length) {
            const before = d.v;
            const m = migrate(d);
            if (m) {
              if (before !== DOC_VERSION) localStorage.setItem(KEY, JSON.stringify(m));
              return m;
            }
          }
        } catch (e) { /* fall through */ }
        return migrateLegacy();
      },
      save(doc) { localStorage.setItem(KEY, JSON.stringify(doc)); },
      clear() { localStorage.removeItem(KEY); },
    };
  }

  /* ---------- change provenance ----------
     Luke, 2026-09-22: record `changed_by` per mutation now, even though
     nothing reads it yet — it cannot be backfilled once the edits have
     happened.

     WHY A DIFF AND NOT A STAMP AT EVERY CALL SITE: a mutation reaches storage
     through ~30 places in project-ui.js (qty steps, typed qty, notes, drag,
     room picker, meta fields, archive, order add...). Stamping each by hand
     would be thirty chances to forget one, and the one that got forgotten
     would be invisible until someone asked "who changed this?" months later.
     Comparing the document about to be saved against the last one we saw
     catches every path, including ones not written yet. The document is
     capped at 512 KB and in practice a few KB, and this runs once per
     debounced save, not per keystroke.

     Only the node's OWN fields are compared — a line moving between rooms
     changes the line (its groupId), not its project. `changed*` fields are
     excluded from the comparison so a stamp is never itself a change. */
  const CHANGE_META = ['changedAt', 'changedById', 'changedByName'];
  function ownFields(node, skipKeys) {
    const out = {};
    Object.keys(node).sort().forEach((k) => {
      if (CHANGE_META.indexOf(k) > -1 || skipKeys.indexOf(k) > -1) return;
      out[k] = node[k];
    });
    return JSON.stringify(out);
  }
  /** id -> own-field signature for every project and line in a doc */
  function signatures(doc) {
    const sig = {};
    if (!doc || !Array.isArray(doc.projects)) return sig;
    doc.projects.forEach((p) => {
      sig['p:' + p.id] = ownFields(p, ['orders']);
      (p.orders || []).forEach((o) => {
        (o.lines || []).forEach((l) => { sig['l:' + l.id] = ownFields(l, []); });
      });
    });
    return sig;
  }
  /**
   * Stamps `changedAt` (in place) on every project and line whose own fields
   * differ from `prevSig`, and returns the new signature map. A node that is
   * new to the document counts as changed — that is when it was created.
   * The server replaces our id claim with the signed customer id; the client
   * only says WHEN and WHICH.
   */
  function stampChanges(doc, prevSig, at) {
    const next = signatures(doc);
    Object.keys(next).forEach((key) => {
      if (prevSig && prevSig[key] === next[key]) return;
      const id = key.slice(2);
      doc.projects.forEach((p) => {
        if (key.charAt(0) === 'p') { if (p.id === id) p.changedAt = at; return; }
        (p.orders || []).forEach((o) => {
          (o.lines || []).forEach((l) => { if (l.id === id) l.changedAt = at; });
        });
      });
    });
    return next;
  }

  /** account login URL carrying a return-to for the current page. New
   *  customer accounts (/customer_authentication/login) honour `return_to`;
   *  the classic /account/login honours `return_url` — both are appended so
   *  either flavour lands the shopper back where they were. */
  function loginUrl(base, returnTo) {
    const target = returnTo || (global.location ? global.location.pathname + global.location.search : '/');
    const sep = base.indexOf('?') > -1 ? '&' : '?';
    return base + sep + 'return_to=' + encodeURIComponent(target) + '&return_url=' + encodeURIComponent(target);
  }

  /* ---------- ProxyAdapter (signed-in customers) ----------
     Local storage stays the fast working store; the server (App Proxy ->
     projects app -> Postgres) is durability. Boot: first login on this
     device POSTs the buffered guest doc to /merge (Odoo guest-cart style —
     the server appends touched projects, drops pristine ones, dedupes
     lines per variant+room) and adopts the merged result; later boots GET
     the account doc. Every save then PUTs the doc, debounced, echoing the
     `_rev` it last read; a 409 means another device moved the doc forward,
     so the server's copy is adopted and repainted.

     Failure posture: everything degrades to the buffer. A 404 (app not
     deployed yet) or a network error leaves the shopper on their local
     doc and reports state 'unavailable' / 'offline' through onState so the
     UI can say "saved on this device". A 401 while the theme says we are
     logged in means the storefront session lapsed: onLoginRequired fires
     and the host redirects to sign in with a return-to.

     SEEING IT ON THE OTHER DEVICE (Luke, 2026-09-09: "no sync engine —
     optimistic local edits plus a background conditional fetch about once a
     minute"). boot() used to be the only read this adapter ever did, so a
     phone and a laptop open at the same time diverged until one reloaded.
     startPolling() adds a conditional GET every `pollMs` (60 s) that sends
     the last ETag: unchanged is a 304 with no body, changed is the document,
     adopted and repainted. It runs only while a list surface is actually on
     screen (the host decides), never while the tab is hidden, and never while
     a local edit is pending or in flight — adopting the server's copy on top
     of an edit the shopper just made would undo it in front of them. Coming
     back from hidden refreshes immediately rather than waiting out the
     interval, which is the case that matters: picking the laptop back up. */
  function ProxyAdapter(opts) {
    opts = opts || {};
    const local = opts.local || LocalAdapter({ key: opts.storageKey });
    const base = (opts.base || '/apps/projects/api').replace(/\/$/, '');
    const customer = opts.customer || {};
    const mergedKey = local.key + ':synced:' + (customer.id || 'me');
    const headers = { 'Content-Type': 'application/json' };
    const fetchImpl = opts.fetch || (global.fetch ? global.fetch.bind(global) : null);
    const onState = opts.onState || (() => {});
    const onAdopt = opts.onAdopt || (() => {});
    const onLoginRequired = opts.onLoginRequired || (() => {});
    const debounceMs = opts.debounceMs == null ? 600 : opts.debounceMs;
    /** background conditional fetch cadence; 0 disables polling entirely */
    const pollMs = opts.pollMs == null ? 60000 : opts.pollMs;
    /** the host says whether a list surface is on screen; default: always */
    const isOpen = opts.isOpen || (() => true);
    const doc$ = global.document || null;
    const adapter = { key: local.key, state: 'local', local: local };
    let putTimer = null;
    let inFlight = null;
    let pollTimer = null;
    let refreshing = false;
    /** validator from the last 200/304; sent as If-None-Match on every poll */
    let etag = null;

    /** own-field signatures of the document as we last saw it, so a save can
     *  tell which nodes this device actually changed (see stampChanges) */
    let sig = null;

    function setState(s) { if (adapter.state !== s) { adapter.state = s; onState(s); } }
    function rememberEtag(res) {
      const tag = res.headers && res.headers.get ? res.headers.get('ETag') : null;
      if (tag) etag = tag;
    }
    function adopt(doc) {
      const m = migrate(doc);
      if (!m) return false;
      local.save(m);
      /* The server's document becomes the new baseline: what it already
         records was changed by someone, not by this device just now. */
      sig = signatures(m);
      onAdopt(m);
      return true;
    }
    async function handleAuth(res) {
      if (res.status === 401) {
        let data = null;
        try { data = await res.json(); } catch (e) { /* no body */ }
        if (data && data.error === 'login_required') { setState('login'); onLoginRequired(); }
        else setState('unavailable');
        return true;
      }
      if (res.status === 404) { setState('unavailable'); return true; }
      return false;
    }
    async function put() {
      if (!fetchImpl) return;
      try {
        const res = await fetchImpl(base + '/doc', {
          method: 'PUT', credentials: 'same-origin', headers,
          /* changedByName is a DISPLAY LABEL only. The server takes identity
             from the signed logged_in_customer_id and ignores any id we send;
             this is just what to show beside the change once editors exist. */
          body: JSON.stringify({ doc: local.read(), changedByName: customer.name || '' }),
        });
        if (await handleAuth(res)) return;
        rememberEtag(res);
        if (res.status === 409) {
          const data = await res.json().catch(() => null);
          if (data && data.doc) adopt(data.doc);
          setState('synced');
        } else if (res.ok) {
          const data = await res.json().catch(() => null);
          if (data && typeof data.rev === 'number') {
            // rev bump only — the server may also have minted ids; take its doc
            if (data.doc) { local.save(migrate(data.doc) || local.read()); }
            else { const doc = local.read(); doc._rev = data.rev; local.save(doc); }
          }
          setState('synced');
        } else if (res.status === 429) {
          setState('synced'); // throttled: the next save retries
        } else {
          setState('unavailable');
        }
      } catch (e) { setState('offline'); }
    }
    adapter.read = () => {
      const doc = local.read();
      if (sig === null) sig = signatures(doc); // first read = the baseline
      return doc;
    };
    adapter.save = (doc) => {
      if (sig === null) sig = signatures(local.read());
      sig = stampChanges(doc, sig, now());
      local.save(doc);
      clearTimeout(putTimer);
      putTimer = setTimeout(() => { putTimer = null; inFlight = put(); }, debounceMs);
    };
    /** flush a pending PUT (tests / beforeunload) */
    adapter.flush = async () => { clearTimeout(putTimer); putTimer = null; inFlight = put(); await inFlight; };

    /** true while this device owes the server an edit — a poll must not adopt
     *  over the top of one, or the shopper watches their own change vanish */
    function localEditPending() { return putTimer !== null || refreshing; }

    /**
     * One conditional GET. 304 = we are current and nothing is repainted;
     * 200 = another device moved the document on, so adopt it (which calls
     * onAdopt and repaints). Returns 'current' | 'adopted' | 'skipped' | the
     * state on failure, so tests and the host can assert on it.
     */
    adapter.refresh = async () => {
      if (!fetchImpl) return 'skipped';
      if (localEditPending()) return 'skipped';
      refreshing = true;
      try {
        const res = await fetchImpl(base + '/doc', {
          credentials: 'same-origin',
          headers: etag ? { 'If-None-Match': etag } : {},
        });
        if (await handleAuth(res)) return adapter.state;
        if (res.status === 304) { rememberEtag(res); setState('synced'); return 'current'; }
        if (!res.ok) { setState('unavailable'); return adapter.state; }
        rememberEtag(res);
        const data = await res.json();
        /* A second device that has not saved anything yet reads doc: null.
           That is "the account has nothing", not "your list was deleted" —
           adopting null would wipe a buffer this device is about to PUT. */
        if (data && data.doc && adopt(data.doc)) { setState('synced'); return 'adopted'; }
        setState('synced');
        return 'current';
      } catch (e) {
        setState('offline');
        return adapter.state;
      } finally { refreshing = false; }
    };

    /** Start the background conditional fetch. Idempotent. */
    adapter.startPolling = () => {
      /* No document = no surface to repaint (the Node test shim, SSR), so
         there is nothing for a poll to achieve and a live interval would
         only keep the process alive. */
      if (pollTimer || !pollMs || !fetchImpl || !doc$) return;
      pollTimer = setInterval(() => {
        if (doc$ && doc$.hidden) return;   // a hidden tab has nothing to repaint
        if (!isOpen()) return;             // no list surface on screen
        adapter.refresh();
      }, pollMs);
      if (doc$ && doc$.addEventListener && !adapter._vis) {
        /* Coming back to the tab is the moment the divergence is visible, so
           refresh then rather than up to a minute later. */
        adapter._vis = () => { if (!doc$.hidden && isOpen()) adapter.refresh(); };
        doc$.addEventListener('visibilitychange', adapter._vis);
      }
    };
    adapter.stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      if (doc$ && adapter._vis) { doc$.removeEventListener('visibilitychange', adapter._vis); adapter._vis = null; }
    };
    /** first authenticated boot on this device merges the buffer; later
     *  boots pull the account doc */
    adapter.boot = async () => {
      if (!fetchImpl) return adapter.state;
      try {
        if (!localStorage.getItem(mergedKey)) {
          const res = await fetchImpl(base + '/merge', {
            method: 'POST', credentials: 'same-origin', headers,
            body: JSON.stringify({ doc: local.read() }),
          });
          if (await handleAuth(res)) return adapter.state;
          rememberEtag(res);
          if (!res.ok) { setState('unavailable'); return adapter.state; }
          const data = await res.json();
          if (data && data.doc && adopt(data.doc)) {
            localStorage.setItem(mergedKey, now());
            setState('synced');
          }
        } else {
          const res = await fetchImpl(base + '/doc', { credentials: 'same-origin' });
          if (await handleAuth(res)) return adapter.state;
          rememberEtag(res);
          if (!res.ok) { setState('unavailable'); return adapter.state; }
          const data = await res.json();
          if (data && data.doc) adopt(data.doc);
          setState('synced');
        }
      } catch (e) { setState('offline'); }
      /* Boot has seeded the ETag; from here the document refreshes itself. */
      adapter.startPolling();
      return adapter.state;
    };
    return adapter;
  }
  /** logout privacy: if a customer previously synced on this device, their
   *  job data (names, sites, POs) must not outlive the session. A present
   *  sync marker with no logged-in customer means a logout happened. */
  ProxyAdapter.clearOnLogout = function (storageKey) {
    const key = storageKey || 'st:projects:v1';
    const markerPrefix = key + ':synced:';
    let hadMarker = false;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.indexOf(markerPrefix) === 0) { hadMarker = true; localStorage.removeItem(k); }
    }
    if (hadMarker) localStorage.removeItem(key);
    return hadMarker;
  };

  function ProjectStore(adapter) {
    const store = {
      read: () => adapter.read(),
      save: (doc) => adapter.save(doc),
      adapter: adapter,
      blankProject: blankProject,
      blankOrder: blankOrder,
      uid: uid,
      now: now,
      ROOM_EXAMPLES: ROOM_EXAMPLES,
      active(doc) {
        const open = doc.projects.filter((x) => x.status !== 'archived');
        return open.find((p) => p.id === doc.activeProjectId) || open[0] || doc.projects[0];
      },
      /** open (non-archived) orders of a project, in seq order */
      openOrders(p) { return p.orders.filter((o) => o.status !== 'archived'); },
      /** the order new saves land in — the project's active open order */
      activeOrder(doc, p) {
        p = p || store.active(doc);
        const open = store.openOrders(p);
        return open.find((o) => o.id === p.activeOrderId) || open[0] || p.orders[0];
      },
      orderById(p, id) { return p.orders.find((o) => o.id === id); },
      /** display name: free-text label, falling back to "Order {seq}" */
      orderName(o) { return o.label || 'Order ' + o.seq; },
      /** paperwork reference: J-1042·2 when a project number exists */
      orderRef(p, o) { return p.projectNumber ? p.projectNumber + '·' + o.seq : ''; },
      lines(doc) { return store.activeOrder(doc).lines; },
      byId(doc, id) { return doc.projects.find((p) => p.id === id); },
      estimate(ls) { return ls.reduce((a, l) => a + (l.price || 0) * (l.qty || 1), 0); },
      /** all open lines of a project, across its orders */
      projectLines(p) { return store.openOrders(p).reduce((a, o) => a.concat(o.lines), []); },
      count(doc) { return store.projectLines(store.active(doc)).reduce((a, l) => a + (l.qty || 1), 0); },
      handles(doc) { return new Set(store.lines(doc).map((l) => l.handle)); },
      lineFrom(d) {
        return {
          id: uid('l'),
          handle: d.handle, variantId: d.variant ? Number(d.variant) : null,
          title: d.title || d.handle, image: d.image || '',
          price: d.price ? Number(d.price) : null, qty: 1, note: '',
          groupId: d.group || null, addedAt: now(),
        };
      },
      /** toggle membership of a product in a project's active order;
       *  returns true when added, false when removed */
      toggleIn(doc, projectId, data) {
        const p = store.byId(doc, projectId) || store.active(doc);
        const ls = store.activeOrder(doc, p).lines;
        const i = ls.findIndex((l) => l.handle === data.handle);
        if (i > -1) { ls.splice(i, 1); return false; }
        ls.push(store.lineFrom(data));
        return true;
      },
      /** where a product (by handle) sits among a project's OPEN orders */
      findInProject(p, handle) {
        const orders = store.openOrders(p);
        for (let i = 0; i < orders.length; i += 1) {
          const l = orders[i].lines.find((x) => x.handle === handle);
          if (l) return { order: orders[i], line: l };
        }
        return null;
      },
      /** Save a product into a SPECIFIC order (and room) of a project — the
       *  save popover's order and room levels (2026-09-17; toggleIn only ever
       *  wrote to the active order). Already in that order: only the room
       *  changes, and only when a groupId is passed (undefined = leave it).
       *  In another open order of the project: the SAME line object moves, so
       *  qty and note survive; its room is cleared unless one is given,
       *  because rooms belong to an order. Not saved: a new line. The project
       *  and the order become the save target. An archived or unknown order
       *  falls back to the active one. */
      saveTo(doc, projectId, orderId, data, groupId) {
        const p = store.byId(doc, projectId) || store.active(doc);
        const asked = store.orderById(p, orderId);
        const o = asked && asked.status !== 'archived' ? asked : store.activeOrder(doc, p);
        const gid = groupId && store.groupById(o, groupId) ? groupId : null;
        const at = store.findInProject(p, data.handle);
        let line; let result;
        if (at && at.order === o) {
          line = at.line; result = 'kept';
          if (groupId !== undefined) line.groupId = gid;
        } else if (at) {
          at.order.lines.splice(at.order.lines.indexOf(at.line), 1);
          line = at.line; line.groupId = gid; o.lines.push(line); result = 'moved';
        } else {
          line = store.lineFrom(data); line.groupId = gid; o.lines.push(line); result = 'added';
        }
        doc.activeProjectId = p.id;
        p.activeOrderId = o.id;
        return { result: result, line: line, order: o, project: p };
      },
      /** remove a product from whichever open order of the project holds it */
      removeFromProject(doc, projectId, handle) {
        const p = store.byId(doc, projectId) || store.active(doc);
        const at = store.findInProject(p, handle);
        if (!at) return false;
        at.order.lines.splice(at.order.lines.indexOf(at.line), 1);
        return true;
      },
      addProject(doc, name) {
        const p = blankProject(uid('p'), name);
        doc.projects.push(p);
        doc.activeProjectId = p.id;
        return p;
      },
      /** mint the next order (seq is monotonic per project) and make it
       *  the save target */
      addOrder(doc, projectId, label) {
        const p = store.byId(doc, projectId) || store.active(doc);
        p.orderSeq = (p.orderSeq || p.orders.length) + 1;
        const o = blankOrder(p.orderSeq);
        if (label) o.label = label;
        p.orders.push(o);
        p.activeOrderId = o.id;
        return o;
      },
      setActiveOrder(doc, projectId, orderId) {
        const p = store.byId(doc, projectId) || store.active(doc);
        if (store.orderById(p, orderId)) p.activeOrderId = orderId;
      },
      /** soft delete an order; a project always keeps one open order */
      archiveOrder(doc, projectId, orderId) {
        const p = store.byId(doc, projectId) || store.active(doc);
        const o = store.orderById(p, orderId);
        if (!o) return;
        o.status = 'archived';
        o.archivedAt = now();
        if (!store.openOrders(p).length) {
          p.orderSeq = (p.orderSeq || 0) + 1;
          p.orders.push(blankOrder(p.orderSeq));
        }
        if (p.activeOrderId === orderId) p.activeOrderId = store.openOrders(p)[0].id;
      },
      /** projects visible in UIs — archived ones stay in the doc for
       *  reporting + recovery but never render */
      activeProjects(doc) { return doc.projects.filter((x) => x.status !== 'archived'); },
      /** soft delete: archive the project (and its items) in place */
      archiveProject(doc, projectId) {
        const p = doc.projects.find((x) => x.id === projectId);
        if (!p) return;
        p.status = 'archived';
        p.archivedAt = now();
        const open = store.activeProjects(doc);
        if (!open.length) doc.projects.push(blankProject(uid('p'), 'My project'));
        if (doc.activeProjectId === projectId) doc.activeProjectId = store.activeProjects(doc)[0].id;
      },
      removeProject(doc, projectId) { store.archiveProject(doc, projectId); },
      setQty(order, index, delta) {
        const l = order.lines[index];
        if (!l) return;
        l.qty = Math.max(0, (l.qty || 1) + delta);
        if (!l.qty) order.lines.splice(index, 1);
      },
      removeLine(order, index) { order.lines.splice(index, 1); },
      /** Undo for removeLine: the same line object back at the index it held
       *  (clamped to the list), so qty, note and room survive untouched */
      restoreLine(order, index, line) {
        if (!line) return;
        if (order.lines.some((l) => l.id === line.id)) return;
        order.lines.splice(Math.max(0, Math.min(index, order.lines.length)), 0, line);
      },
      lineById(order, id) { return order.lines.find((l) => l.id === id); },

      /* ---------- room groups ---------- */
      /** groups of an order in drag order (position, then insertion) */
      groupsOf(order) {
        if (!Array.isArray(order.groups)) order.groups = [];
        return order.groups.slice().sort((a, b) => (a.position || 0) - (b.position || 0));
      },
      groupById(order, id) { return (order.groups || []).find((g) => g.id === id); },
      /** renumber positions on the step so they stay unique + gapped */
      renumberGroups(order) {
        store.groupsOf(order).forEach((g, i) => { g.position = (i + 1) * POSITION_STEP; });
      },
      /** next placeholder example: the first one not already used as a label */
      nextRoomExample(order) {
        const used = new Set((order.groups || []).map((g) => (g.label || '').trim().toLowerCase()));
        return ROOM_EXAMPLES.find((x) => !used.has(x.toLowerCase())) || ROOM_EXAMPLES[(order.groups || []).length % ROOM_EXAMPLES.length];
      },
      addGroup(order, label) {
        if (!Array.isArray(order.groups)) order.groups = [];
        const last = store.groupsOf(order).slice(-1)[0];
        const g = { id: uid('g'), label: (label || '').trim(), position: (last ? last.position : 0) + POSITION_STEP, collapsed: false };
        order.groups.push(g);
        return g;
      },
      renameGroup(order, groupId, label) {
        const g = store.groupById(order, groupId);
        if (g) g.label = (label || '').trim().slice(0, 120);
      },
      toggleGroup(order, groupId) {
        const g = store.groupById(order, groupId);
        if (g) g.collapsed = !g.collapsed;
      },
      /** delete a room: its lines fall back to Unassigned, never lost */
      removeGroup(order, groupId) {
        order.groups = (order.groups || []).filter((g) => g.id !== groupId);
        order.lines.forEach((l) => { if (l.groupId === groupId) l.groupId = null; });
        store.renumberGroups(order);
      },
      /** drag order: move a group to index (0-based in the sorted list) */
      moveGroup(order, groupId, toIndex) {
        const sorted = store.groupsOf(order);
        const from = sorted.findIndex((g) => g.id === groupId);
        if (from < 0) return;
        const g = sorted.splice(from, 1)[0];
        sorted.splice(Math.max(0, Math.min(sorted.length, toIndex)), 0, g);
        sorted.forEach((x, i) => { x.position = (i + 1) * POSITION_STEP; });
      },
      /** put a line in a room (null = Unassigned) */
      moveLine(order, lineId, groupId) {
        const l = store.lineById(order, lineId);
        if (!l) return;
        l.groupId = groupId && store.groupById(order, groupId) ? groupId : null;
      },
      /** Move a line anywhere in its project: another room, another order,
       *  a position among its new siblings (drag and drop + the room picker,
       *  2026-09-17). The SAME line object moves, so qty and note survive.
       *  groupId must be a room of the TARGET order or the line lands in
       *  "No room"; beforeLineId places it before that sibling, else last.
       *  Returns the line, or null when nothing moved. */
      moveLineTo(doc, projectId, fromOrderId, lineId, toOrderId, groupId, beforeLineId) {
        const p = store.byId(doc, projectId);
        if (!p) return null;
        const from = store.orderById(p, fromOrderId);
        const to = store.orderById(p, toOrderId) || from;
        if (!from || !to || to.status === 'archived') return null;
        const i = from.lines.findIndex((l) => l.id === lineId);
        if (i < 0) return null;
        const line = from.lines.splice(i, 1)[0];
        line.groupId = groupId && store.groupById(to, groupId) ? groupId : null;
        const at = beforeLineId ? to.lines.findIndex((l) => l.id === beforeLineId) : -1;
        if (at < 0) to.lines.push(line); else to.lines.splice(at, 0, line);
        return line;
      },
      /** the room picker's "Create room '<typed>'": mint the room in the
       *  line's own order and put the line in it, one step */
      createRoomFor(doc, projectId, orderId, lineId, label) {
        const p = store.byId(doc, projectId);
        const o = p && store.orderById(p, orderId);
        if (!o || !store.lineById(o, lineId)) return null;
        const g = store.addGroup(o, label);
        store.moveLine(o, lineId, g.id);
        return g;
      },
      /** lines bucketed by room, Unassigned first — the render order */
      linesByGroup(order) {
        const groups = store.groupsOf(order);
        const known = new Set(groups.map((g) => g.id));
        const buckets = [{ group: null, lines: [] }].concat(groups.map((g) => ({ group: g, lines: [] })));
        order.lines.forEach((l) => {
          const gi = l.groupId && known.has(l.groupId) ? groups.findIndex((g) => g.id === l.groupId) + 1 : 0;
          buckets[gi].lines.push(l);
        });
        return buckets;
      },

      /** What "See details" discloses, looked up lazily (2026-09-17): a saved
       *  line only ever stored { handle, variantId, title, image, price, qty,
       *  note }, so the Lists row had nothing to put behind the cart's
       *  expander. One /products/{handle}.js per line (the lookup
       *  resolveVariants already uses) yields the variant's title (when the
       *  product has real variants) and SKU. Returns PATCHES keyed by line
       *  id — never mutates — because the fetch is async and the caller must
       *  apply onto a FRESH doc (the shopper may have edited meanwhile). */
      async lookupDetails(lines, fetchImpl) {
        const f = fetchImpl || global.fetch.bind(global);
        const out = [];
        for (const l of lines) {
          try {
            const prod = await f('/products/' + l.handle + '.js').then((r) => r.json());
            const vs = prod.variants || [];
            const v = (l.variantId && vs.find((x) => x.id === l.variantId)) || vs.find((x) => x.available) || vs[0];
            if (!v) continue;
            const real = vs.length > 1 || (v.title && v.title !== 'Default Title');
            out.push({ id: l.id, patch: { variantId: v.id, variantTitle: real ? (v.title || '') : '', sku: v.sku || '', price: v.price } });
          } catch (e) { /* leave it; the caller will not retry this session */ }
        }
        return out;
      },
      /** apply lookupDetails patches onto a doc by line id; dx marks a line
       *  as looked up. A price already stored is kept (it is what the shopper
       *  saw when saving); a missing one is filled. Returns lines touched. */
      applyDetails(doc, patches) {
        let n = 0;
        const byId = {};
        (patches || []).forEach((x) => { byId[x.id] = x.patch; });
        doc.projects.forEach((p) => p.orders.forEach((o) => o.lines.forEach((l) => {
          const x = byId[l.id];
          if (!x) return;
          if (!l.variantId) l.variantId = x.variantId;
          l.variantTitle = x.variantTitle || '';
          l.sku = x.sku || '';
          if (l.price == null && x.price != null) l.price = x.price;
          l.dx = 1;
          n += 1;
        })));
        return n;
      },
      /** resolve any lines missing a variant id via /products/{handle}.js */
      async resolveVariants(lines, fetchImpl) {
        const f = fetchImpl || global.fetch.bind(global);
        for (const l of lines) {
          if (l.variantId) continue;
          try {
            const prod = await f('/products/' + l.handle + '.js').then((r) => r.json());
            const v = (prod.variants.find((x) => x.available) || prod.variants[0]);
            l.variantId = v && v.id;
            if (l.price == null && v) l.price = v.price;
          } catch (e) { /* leave unresolved; caller filters */ }
        }
      },
    };
    return store;
  }

  global.ProjectCore = {
    ProjectStore: ProjectStore, LocalAdapter: LocalAdapter, ProxyAdapter: ProxyAdapter,
    blankProject: blankProject, blankOrder: blankOrder, blankDoc: blankDoc,
    migrateV1: migrateV1, migrateV2: migrateV2, migrate: migrate, loginUrl: loginUrl,
    signatures: signatures, stampChanges: stampChanges,
    DOC_VERSION: DOC_VERSION, ROOM_EXAMPLES: ROOM_EXAMPLES,
  };
})(window);
/* project-ui.js — renderer + interactions for the projects concept.
 * Theme-agnostic: everything a host theme might restyle or rewire comes
 * through the config (selectors, routes, money, cart integration, copy).
 * Defaults work on a stock theme: save buttons toggle a popout, a drawer
 * root and a board root render if present, add-all falls back to /cart.
 *
 * Composition contract (all optional in the host page):
 *   [data-project-root]   drawer mount     [data-project-board] board mount
 *   [data-project-count]  badge(s)         [data-wishlist]/[data-add-project] save buttons
 *   [data-open-project]   opens the drawer
 * Events out: 'project:change' (doc mutated), 'project:kit-added' (cart add,
 * detail:{items}; preventDefault() to suppress the /cart fallback redirect).
 * Events in: none required; host may call ProjectListUI(config) manually
 * after setting window.PROJECT_MANUAL_INIT = true before the script loads.
 */
(function (global) {
  'use strict';

  function ProjectListUI(userCfg) {
    const core = global.ProjectCore;
    if (!core) return null;
    const cfg = Object.assign({
      adapter: null,
      proxyBase: null,   // e.g. '/apps/projects/api' — enables server sync (ProxyAdapter) when logged in
      /* Share links live on the same app proxy. Defaults to proxyBase, so a
         store that has sync has sharing; set it to false to keep the Share
         button hidden while sync is on (a store that has not had the theme's
         share styles vendored yet). */
      shareBase: undefined,
      guestBuffer: true, // RFC §4: guests may save into the local buffer (merged on sign-in); false = hard gate to login
      storageKey: 'st:projects:v1',
      currency: 'AUD',
      locale: 'en-AU',
      boardUrl: '/pages/project',
      continueUrl: '/collections/all', // the page's "Continue shopping" (the drawer's just closes)
      productBase: '/products/',
      customer: global.ST_CUSTOMER || null, // {logged_in, login, register}
      openDrawer: null,     // fn; default dispatches 'flyout:open' detail 'project'
      addToCart: null,      // async fn(items) -> bool handled; default POST /cart/add.js
      afterKitAdd: null,    // fn(cart items) after successful add; default: redirect /cart unless a 'project:kit-added' listener preventDefault()s
      classes: {},          // class-name overrides, keys as used below
      texts: {},
    }, userCfg || {});

    /* ---------- storage + login gate ----------
       Guests: LocalAdapter is the pre-login BUFFER (RFC §4 recommendation).
       Signed in + proxyBase: ProxyAdapter wraps the same local store and syncs
       it to the projects app; the server never accepts an anonymous write, so
       there is nothing to gate server-side — the gate here is UX: what a guest
       may do before signing in (buffer saves) and what needs an account
       (named projects, rooms). */
    if (cfg.shareBase === undefined) cfg.shareBase = cfg.proxyBase;
    const loggedIn = !!(cfg.customer && cfg.customer.logged_in);
    const guestBuffer = cfg.guestBuffer !== false;
    const loginHref = (returnTo) => core.loginUrl((cfg.customer && cfg.customer.login) || '/account/login', returnTo);
    const gotoLogin = () => { location.href = loginHref(); };
    /** true when the shopper may proceed; otherwise sends them to sign in */
    const requireLogin = () => { if (loggedIn) return true; gotoLogin(); return false; };
    let syncState = 'local';
    let adapter = cfg.adapter || null;
    if (!adapter && cfg.proxyBase && !loggedIn) core.ProxyAdapter.clearOnLogout(cfg.storageKey);
    if (!adapter && cfg.proxyBase && loggedIn) {
      adapter = core.ProxyAdapter({
        base: cfg.proxyBase, storageKey: cfg.storageKey, customer: cfg.customer,
        onState: (s) => { syncState = s; paintSync(); document.dispatchEvent(new CustomEvent('project:sync', { detail: s })); },
        onAdopt: () => { paint(); renderAll(); },
        onLoginRequired: gotoLogin,
        /* The background conditional fetch (60 s) only runs while a list
           surface is actually on screen. `offsetParent === null` is true for
           a closed flyout (it or an ancestor is display:none), so this needs
           no knowledge of the theme's open/close event names — which differ
           between the drawer, the project page and the cart page. */
        isOpen: () => {
          const board = document.querySelector('[data-project-board]');
          if (board && board.offsetParent !== null) return true;
          const root = document.querySelector('[data-project-root]');
          return !!(root && root.offsetParent !== null);
        },
      });
    }
    const store = core.ProjectStore(adapter || core.LocalAdapter({ key: cfg.storageKey }));
    const fmt = new Intl.NumberFormat(cfg.locale, { style: 'currency', currency: cfg.currency });
    const money = cfg.money || ((cents) => (cents || cents === 0) ? fmt.format(cents / 100) : '');
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const T = Object.assign({
      saveTo: 'Save to project', newProject: '+ New project', newProjectShort: 'New project',
      openList: 'Open project list', inProject: 'In your project', addProject: 'Add to project',
      emptyDrawer: 'Nothing in this project yet.<br>Save fixtures from any product card or page.',
      continueShopping: 'Continue shopping', estimated: 'Estimated total', addAll: 'Add all to cart',
      viewAll: 'View all projects', deleteProject: 'Delete project', confirmDelete: 'Confirm delete',
      emptyBoard: 'Nothing saved to this project yet.',
      authNudge: 'Saved on this device only \u2014 {login} or {register} to keep it.',
      authLogin: 'sign in', authRegister: 'create a free account',
      guestNotice: 'Sign in to save this project', signIn: 'Sign in', createAccount: 'Create account',
      gateTitle: 'Sign in to save this project',
      gateBody: 'Projects are saved to your account so they follow you across devices and can be organised by room. Anything you save before signing in stays on this device until then.',
      gateEmpty: 'Nothing saved on this device yet. Sign in, then save fixtures from any product card or page.',
      syncOffline: 'Offline \u2014 saved on this device; syncs when you are back online.',
      syncUnavailable: 'Saved on this device only for now.',
      unassigned: 'Unassigned', addRoom: '+ Add room', roomPh: 'e.g. ', roomLabel: 'Room name',
      roomHint: 'Add a room to organise what goes where \u2014 plumbers and installers love this.',
      roomExamples: 'e.g. Ensuite, Master bathroom, Kitchen, Laundry, Outdoor shower',
      deleteRoom: 'Remove room (items move to Unassigned)', collapseRoom: 'Collapse room', expandRoom: 'Expand room',
      moveTo: 'Move to\u2026', dragRoom: 'Drag to reorder', dropHere: 'Drag items here, or use \u201cMove to\u201d on an item.',
      metaName: 'Project name', metaNamePh: 'e.g. Smith reno', metaProject: 'Project #',
      metaProjectPh: 'e.g. J-1042', metaPo: 'PO #', metaPoPh: 'customer PO',
      metaQuote: 'Quote #', metaQuotePh: 'customer quote', metaCustomer: 'Customer', metaCustomerPh: 'customer / site',
      items: 'item', itemsPlural: 'items',
      comboPlaceholder: 'Search or create a project\u2026',
      comboCreate: 'Create', comboNone: 'No projects yet \u2014 type a name and press Enter.',
      comboNoneCustomer: 'No customers yet \u2014 type a name and press Enter.',
      comboProject: 'project', comboProjects: 'projects',
      popSearchPh: 'Search or create\u2026', popAdd: 'Add to this project', popRemove: 'Remove from this project',
      confirmTitle: 'Delete this project?',
      confirmBody: '{name} and its {count} will be removed from your lists. We keep an archived copy, so it can be restored for you later if needed.',
      confirmYes: 'Delete project', confirmNo: 'Keep it',
      orderFallback: 'Order', newOrder: '+ New order', orderLabelPh: 'name this order (optional)',
      savingHere: 'Saving here', deleteOrder: 'Delete order',
      addOrderCta: 'Add order to cart',
      addOrderThis: 'Add this order to cart', addOrderNamed: 'Add {order} to cart', addAllOrders: 'Add all orders to cart',
      keepTitle: 'Don\u2019t lose your list.', keepBody: 'Sign in to keep it, share it and see it on any device.',
      keepNote: 'Saved on this device only until you sign in \u00b7 anything you add now comes with you',
      syncedNote: 'Synced to your account',
      metaReady: 'Ready by', readyTip: 'When this order needs to be on site \u2014 helps Sydney Taps plan stock and lets you plan the job',
      readyChip: 'Ready {date}', poChip: 'PO {po}',
      chat: 'Chat', chatTip: 'Chat with the showroom \u2014 product questions, stock and quotes',
      shareTip: 'Share this project with a builder or architect', share: 'Share this project',
      shareTitle: 'Share this list', shareOn: 'Anyone with the link can view this list',
      shareOff: 'Only you can see this list',
      shareEnable: 'Create a link', shareDisable: 'Turn the link off',
      shareCopy: 'Copy link', shareCopied: 'Copied',
      shareOnce: 'Copy it now — for your security the link is not stored anywhere we can show it to you again. Turning it off and on makes a NEW link and the old one stops working.',
      shareLive: 'A link is active. If you have lost it, turn it off and create a new one.',
      shareBusy: 'Working…', shareFailed: 'Could not reach the sharing service. Try again in a moment.',
      shareClose: 'Close',
      notePh: 'add a note', noteLabel: 'Line note',
      ordersWord: 'order', ordersPlural: 'orders',
      cartTitle: 'Replace cart contents?',
      cartBody: 'Your cart already holds {count}. Replace it with {order}? One order per checkout keeps its PO on the paperwork.',
      cartReplace: 'Replace cart', cartAppend: 'Add anyway',
      confirmTitleOrder: 'Delete this order?',
      confirmBodyOrder: '{name} and its {count} will be removed from {project}. We keep an archived copy, so it can be restored for you later if needed.',
      confirmYesOrder: 'Delete order',
      projectTotal: 'Project total',
      addedToList: 'Added to list', addedShort: 'Added', removed: 'Removed', undo: 'Undo',
      seeDetails: 'See details', noteTitle: 'Line note',
      dragLine: 'Drag to a room or another order', roomBadgeTip: 'Change room', roomPickTitle: 'Room', roomPickPh: 'Search or create a room\u2026',
      roomCreate: 'Create room', roomNone: 'No rooms yet \u2014 type a name and press Enter.', dropNewRoom: 'Drop here for a new room',
      popOrderHead: 'Order', popRoomHead: 'Room', popIn: 'in', noRoom: 'No room', newRoom: '+ New room', newRoomPh: 'room name',
      popChoose: 'Choose order and room', roomsGate: 'Rooms are kept in your account.', roomsGateLink: 'to organise by room',
      viewProjects: 'Projects', viewCustomers: 'Customers',
      noCustomer: 'No customer set', custProjects: 'projects', openProject: 'Open',
    }, cfg.texts);

    function emitChange() { document.dispatchEvent(new CustomEvent('project:change')); }
    const openDrawer = cfg.openDrawer || (() => document.dispatchEvent(new CustomEvent('flyout:open', { detail: 'project' })));

    /* ---------- paint ---------- */
    function paint() {
      const doc = store.read();
      const n = store.count(doc);
      document.querySelectorAll('[data-project-count]').forEach((el) => {
        // write only on change — unconditional textContent writes fire
        // childList mutations every paint, and paint() itself runs off a
        // body MutationObserver: a perpetual mutation heartbeat that starves
        // other debounced observers on the page
        if (el.textContent !== String(n)) el.textContent = n;
        if (el.hidden !== (n === 0)) el.hidden = n === 0;
      });
      const handles = store.handles(doc);
      document.querySelectorAll('[data-wishlist],[data-add-project]').forEach((b) => {
        const on = handles.has(b.dataset.handle);
        b.classList.toggle('is-active', on);
        if (b.hasAttribute('data-add-project')) b.textContent = on ? T.inProject : T.addProject;
      });
    }

    /* ---------- shared markup ---------- */
    const AUTH_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-3.8 4.5-6 8-6s6.5 2.2 8 6"/></svg>';
    /** one-line notice: guests = "Sign in to save this project" (buffer stays
     *  on the device); signed-in = sync state when it is not plain synced */
    /* The drawer's sign-in block, directly under the head (Luke, 2026-09-17:
     * "sign in block should be up the top just below the Lists header").
     * One strip in the promise-bar idiom of the cart, then a one-line note;
     * signed in, only the sync notice (hidden while synced). */
    function authNudge() {
      const c = cfg.customer || {};
      if (loggedIn) return syncNotice();
      return '<div class="pauth pauth--strip">' + AUTH_ICON +
        '<p><b>' + T.keepTitle + '</b> ' + T.keepBody + '</p>' +
        '<span class="pauth__acts"><a class="btn" href="' + esc(loginHref()) + '">' + T.signIn + '</a>' +
        '<a class="btn btn--ghost" href="' + esc(c.register || '/account/register') + '">' + T.createAccount + '</a></span></div>' +
        '<p class="pdrawer__note">' + T.keepNote + '</p>';
    }
    function syncText() {
      if (!cfg.proxyBase || syncState === 'synced' || syncState === 'local') return '';
      return syncState === 'offline' ? T.syncOffline : T.syncUnavailable;
    }
    function syncNotice() {
      const msg = syncText();
      return '<div class="pauth pauth--sync" data-psync' + (msg ? '' : ' hidden') + '>' + AUTH_ICON + '<span>' + msg + '</span></div>';
    }
    /** state changes repaint only the notice — never the whole board (focus) */
    function paintSync() {
      const msg = syncText();
      document.querySelectorAll('[data-psync]').forEach((el) => {
        el.hidden = !msg;
        const span = el.querySelector('span');
        if (span && span.textContent !== msg) span.textContent = msg;
      });
    }
    function metaField(label, key, value, ph, cls) {
      return '<label class="pdrawer__meta-field' + (cls ? ' ' + cls : '') + '"><span>' + label + '</span>' +
        '<input type="text" data-pmeta="' + key + '" value="' + esc(value) + '" placeholder="' + ph + '"></label>';
    }
    /* project-level meta: project # + customer. PO / quote live per-order. */
    function metaGrid(p, o) {
      o = o || {};
      return '<div class="pdrawer__meta pdrawer__meta--project">' +
        (o.noName ? '' : metaField(T.metaName, 'name', p.name, T.metaNamePh, 'pdrawer__meta-field--wide')) +
        metaField(T.metaProject, 'projectNumber', p.projectNumber, T.metaProjectPh) +
        (o.customerCombo
          ? '<label class="pdrawer__meta-field pdrawer__meta-field--combo"><span>' + T.metaCustomer + '</span>' + comboHTML('customer', p.customerRef || '', T.metaCustomerPh) + '</label>'
          : metaField(T.metaCustomer, 'customerRef', p.customerRef, T.metaCustomerPh)) +
        '</div>';
    }
    /** one saved line. `i` is its index in the ORDER's lines (setQty/remove
     *  address by index); with rooms present the row is draggable and gets a
     *  "Move to" select as the keyboard / touch path. */
    /* "See details", the cart row's expander (Luke, 2026-09-17: "we seem to
       be missing 'Details +' that we have in the cart product row"): variant
       and SKU, rendered only when there is something in it, as the cart
       does. The data arrives lazily (backfillDetails). Open state survives
       the innerHTML re-renders through openDetails. */
    const openDetails = new Set();
    function detailsHTML(l) {
      if (!l.variantTitle && !l.sku) return '';
      return '<details class="cart-line__details pline__details" data-pline-details="' + esc(l.id || '') + '"' + (openDetails.has(l.id) ? ' open' : '') + '>' +
        '<summary>' + T.seeDetails + '</summary>' +
        (l.variantTitle ? '<p class="cart-line__variant">' + esc(l.variantTitle) + '</p>' : '') +
        (l.sku ? '<p class="cart-line__sku">SKU ' + esc(l.sku) + '</p>' : '') +
        '</details>';
    }
    document.addEventListener('toggle', (e) => {
      const d = e.target && e.target.closest ? e.target.closest('[data-pline-details]') : null;
      if (!d) return;
      if (d.open) openDetails.add(d.dataset.plineDetails); else openDetails.delete(d.dataset.plineDetails);
    }, true);
    /* lazy backfill: lines saved before 2026-09-17 (and new ones) carry no
       variant title or SKU; look them up eight at a time after a render,
       apply onto a FRESH doc, render once. detailTried stops a failed
       lookup from being retried this session. */
    let detailBusy = false;
    const detailTried = new Set();
    function backfillDetails() {
      if (detailBusy || !store.lookupDetails) return;
      const need = [];
      const doc0 = store.read();
      store.activeProjects(doc0).forEach((p) => store.projectLines(p).forEach((l) => {
        if (!l.dx && l.id && l.handle && !detailTried.has(l.id) && need.length < 8) need.push({ id: l.id, handle: l.handle, variantId: l.variantId });
      }));
      if (!need.length) return;
      detailBusy = true;
      need.forEach((l) => detailTried.add(l.id));
      store.lookupDetails(need).then((patches) => {
        detailBusy = false;
        if (!patches.length) return;
        const doc = store.read();
        if (store.applyDetails(doc, patches)) { store.save(doc); renderAll(); }
      }, () => { detailBusy = false; });
    }
    function lineHTML(l, i, o, hasGroups) {
      const groups = hasGroups ? store.groupsOf(o) : [];
      const room = l.groupId ? store.groupById(o, l.groupId) : null;
      return '<div class="pline" data-pline="' + i + '" data-pline-id="' + esc(l.id || '') + '">' +
        /* the drag affordance: pointer-only (the room badge is the keyboard
           and screen-reader path), so text selection and links keep working */
        '<span class="pline__grip" data-pline-grip aria-hidden="true" title="' + esc(T.dragLine) + '">' + GRIP + '</span>' +
        (l.image ? '<img src="' + esc(l.image) + '" alt="" loading="lazy">' : '<span class="pline__noimg"></span>') +
        '<div class="pline__info">' +
        '<a href="' + cfg.productBase + esc(l.handle) + '" title="' + esc(l.title) + '">' + esc(l.title) + '</a>' +
        '<div class="pline__ctl">' + detailsHTML(l) + '<div class="pline__qty">' +
        '<button type="button" data-pline-qty="-1" aria-label="Decrease quantity">&minus;</button>' +
        /* typed, not just stepped — the cart's QtyInput semantics (Luke,
           2026-09-17: "I can't type a number in the quantity widget in the
           list drawer cart, we need these components to behave identically
           between cart and list cart"): empty allowed while typing, every
           valid integer commits at once, blur resets the draft, 0 removes
           through the same Undo path as the X */
        '<input type="number" class="pline__qty-field" data-pline-qtyin value="' + (l.qty || 1) + '" min="0" inputmode="numeric" aria-label="Quantity">' +
        '<button type="button" data-pline-qty="1" aria-label="Increase quantity">+</button>' +
        '</div>' +
        /* the room: one badge that SHOWS the assignment ("Unassigned" or the
           room's name, the PO / READY chip dress) and CHANGES it — it opens
           the pick-or-create room picker. It replaces the per-line "move to"
           <select> (Luke, 2026-09-17: "instead of a dropdown selection for
           unassigned we should just have a badge"). */
        '<button type="button" class="porder__chip pline__room' + (room ? '' : ' is-none') + '" data-pline-room aria-haspopup="dialog" aria-expanded="false" title="' + esc(T.roomBadgeTip) + '">' + esc(room ? (room.label || T.roomLabel) : T.unassigned) + '</button>' +
        /* the line note: a one-line button (ellipsis, the full text on
           title=) that opens the theme's shared multiline editor
           (SydneyTaps.noteModal, the cart's); without it the click swaps in
           the old inline field. Carried to the cart as the "Note" property. */
        '<button type="button" class="pline__note cart-line__note' + (l.note ? ' has-note' : '') + '" data-pline-note-btn title="' + esc(l.note || '') + '" aria-label="' + T.noteLabel + '">' + esc(l.note ? l.note.replace(/\s+/g, ' ') : T.notePh) + '</button>' +
        '</div></div>' +
        (l.price != null ? '<b class="pline__price">' + rollHTML('l:' + (l.id || l.handle + ':' + i), money((l.price || 0) * (l.qty || 1))) + '</b>' : '<span></span>') +
        '<button class="pline__remove" type="button" data-pline-remove aria-label="Remove">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" style="width:14px;height:14px"><path d="m6 6 12 12M18 6 6 18"/></svg></button>' +
        '</div>';
    }

    /* ---------- room groups (doc v3) ----------
       An order's lines render bucketed by room: "Unassigned" first (header
       only once a room exists), then each room in drag order. Room header =
       grip (drag handle) + editable label with a rotating placeholder
       example + count/subtotal + collapse + remove. Lines drag between
       buckets; the "Move to" select on each line is the keyboard/touch path. */
    const GRIP = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>';
    const CARET = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9.5 6 6 6-6"/></svg>';
    function roomSum(n, est, key) { return n + ' ' + (n === 1 ? T.items : T.itemsPlural) + (est ? ' \u00b7 ' + rollHTML(key, money(est)) : ''); }
    function groupedLines(o) {
      const buckets = store.linesByGroup(o);
      const hasGroups = buckets.length > 1;
      return buckets.map((b, bi) => {
        const g = b.group;
        const gid = g ? g.id : '';
        const n = b.lines.length;
        let html = '<section class="pgroup' + (g ? '' : ' pgroup--unassigned') + (g && g.collapsed ? ' is-collapsed' : '') + '" data-pgroup="' + esc(gid) + '" data-pgroup-index="' + (bi - 1) + '">';
        if (g) {
          html += '<header class="pgroup__head" draggable="true" data-pgroup-drag="' + esc(gid) + '">' +
            '<span class="pgroup__grip" title="' + T.dragRoom + '" aria-hidden="true">' + GRIP + '</span>' +
            '<input class="pgroup__label" type="text" data-pgroup-label="' + esc(gid) + '" value="' + esc(g.label) + '" placeholder="' + esc(T.roomPh + store.nextRoomExample(o)) + '" maxlength="120" aria-label="' + T.roomLabel + '" autocomplete="off" spellcheck="false">' +
            '<span class="pgroup__sum">' + roomSum(n, store.estimate(b.lines), 'g:' + gid) + '</span>' +
            '<button type="button" class="pgroup__toggle" data-pgroup-toggle="' + esc(gid) + '" aria-expanded="' + (!g.collapsed) + '" aria-label="' + (g.collapsed ? T.expandRoom : T.collapseRoom) + '">' + CARET + '</button>' +
            '<span class="pcombo__icon pgroup__del" role="button" tabindex="0" data-pgroup-del="' + esc(gid) + '" title="' + T.deleteRoom + '">' + IC.trash + '</span>' +
            '</header>';
        } else if (hasGroups) {
          html += '<header class="pgroup__head pgroup__head--unassigned"><span class="pgroup__name">' + T.unassigned + '</span>' +
            '<span class="pgroup__sum">' + roomSum(n, store.estimate(b.lines), 'g:un:' + o.id) + '</span></header>';
        }
        html += '<div class="pgroup__lines" data-pgroup-drop="' + esc(gid) + '">' +
          bucketLines(b, o, hasGroups, gid) +
          (n || !hasGroups ? '' : '<p class="pgroup__empty">' + T.dropHere + '</p>') +
          '</div></section>';
        return html;
      }).join('');
    }
    /** a bucket's rows with the Undo ghosts that belong in it: a ghost sits
     *  where its line was — before the first row now holding an index at or
     *  past the removed one, else at the end of its room; a ghost whose room
     *  is gone falls back to Unassigned */
    function bucketLines(b, o, hasGroups, gid) {
      const rooms = store.groupsOf(o).map((g) => g.id);
      const mine = ghosts.filter((g) => g.oid === o.id && ((g.line.groupId && rooms.indexOf(g.line.groupId) > -1 ? g.line.groupId : '') === gid))
        .sort((a, c) => a.index - c.index);
      let gi = 0; let out = '';
      b.lines.forEach((l) => {
        const idx = o.lines.indexOf(l);
        while (gi < mine.length && mine[gi].index <= idx) { out += ghostHTML(mine[gi]); gi += 1; }
        out += lineHTML(l, idx, o, hasGroups);
      });
      while (gi < mine.length) { out += ghostHTML(mine[gi]); gi += 1; }
      return out;
    }
    /** "+ Add room" with the placeholder examples; first-run hint while
     *  the order has no rooms yet */
    function roomAdd(o) {
      const first = !store.groupsOf(o).length;
      return '<div class="pgroup__add" data-pgroup-newdrop="' + esc(o.id) + '" data-drop-label="' + esc(T.dropNewRoom) + '">' +
        '<button type="button" class="pgroup__addbtn" data-pgroup-new="' + esc(o.id) + '">' + T.addRoom + '</button>' +
        '<span class="pgroup__hint">' + (first ? T.roomHint + ' ' : '') + T.roomExamples + '</span></div>';
    }
    function focusGroupLabel(gid, within) {
      const el = (within && document.contains(within) ? within : document).querySelector('[data-pgroup-label="' + gid + '"]');
      if (el) { el.focus(); }
    }

    /* ---------- combobox (Odoo many2one-style; kinds: project, customer) --- */
    const IC = {
      chat: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-5 4v-4H6.5A2.5 2.5 0 0 1 4 13.5z"/></svg>',
      share: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12v7h16v-7M12 3v13M8 7l4-4 4 4"/></svg>',
      pencil: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 16.5 4 20z"/><path d="m13.5 6.5 3 3"/></svg>',
      trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M10 11v6M14 11v6M6.5 7l1 12a2 2 0 0 0 2 1.6h5a2 2 0 0 0 2-1.6l1-12M9.5 7V5a1.8 1.8 0 0 1 1.8-1.8h1.4A1.8 1.8 0 0 1 14.5 5v2"/></svg>',
      tick: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>',
    };
    /* ---------- motion (cart mock v4, Luke 2026-09-17: "ensure we have the
       same concept built into the list/project cart as well") ----------
       Three behaviours, the cart island's contract (theme PR #348):
       1. every money figure that changes ROLLS digit by digit through
          window.SydneyTaps.roll (roll.js; absent = plain text). This UI
          re-renders whole roots with innerHTML, so a roll is keyed
          ([data-roll]) and the previous text is snapshotted before the
          render and handed to the new node, which then rolls to the new
          value on the next frame.
       2. "Added to list" is a STATE of the head (.hconfirm inside the Lists
          drawer head and the project page head; CSS in the theme's cart
          motion block): sweep, tick, hold, flip away; 2.4 s; every add.
       3. removing a row: the row tints, slides left and collapses
          (.pline.is-removing, 380 ms), then a quiet "Removed · title · Undo"
          ghost holds its place for 5 s; Undo puts the line back at the same
          index with the same qty/note/room (store.restoreLine). */
    const REDUCED = !!(global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const rollApi = () => (global.SydneyTaps && global.SydneyTaps.roll) || null;
    function rollHTML(key, text) { return '<span class="roll" data-roll="' + esc(key) + '">' + esc(text) + '</span>'; }
    function rollSnapshot(root) {
      const m = {};
      if (root) root.querySelectorAll('[data-roll]').forEach((el) => { m[el.dataset.roll] = el.dataset.text != null ? el.dataset.text : el.textContent; });
      return m;
    }
    function rollPaint(root, prev) {
      const api = rollApi();
      if (!api || !root) return;
      root.querySelectorAll('[data-roll]').forEach((el) => {
        const next = el.textContent;
        const was = prev ? prev[el.dataset.roll] : null;
        if (REDUCED || was == null || was === next) { api.mount(el, next); return; }
        api.mount(el, was);
        requestAnimationFrame(() => api.set(el, next));
      });
    }
    /* the theme's cart island is a deferred module and usually lands AFTER
       this script's first render (measured on the #349 preview: 0 mounted
       rolls on the page's first paint, rolls on every later render): when
       it announces itself, mount the figures already on screen so the first
       change rolls from a mounted number rather than from plain text */
    document.addEventListener('st:cart:ready', () => {
      const api = rollApi();
      if (!api) return;
      document.querySelectorAll('[data-roll]').forEach((el) => { if (el.dataset.text == null) api.mount(el, el.textContent); });
    });
    const HCONFIRM = '<div class="hconfirm" aria-live="polite">' + IC.tick + '<span class="hconfirm__t">' + T.addedToList + '</span><span class="hconfirm__n"></span></div>';
    const confirmState = { t: null, n: 0 };
    /** the Lists drawer head (SSR in the header) and the project page head */
    function confirmHeads() {
      return [document.querySelector('[data-flyout="project"] .flyout__head'), document.querySelector('.pboard__top')].filter(Boolean);
    }
    /** `where` names the destination when the save popover chose one —
     *  "Added · Order 2 · Ensuite" — otherwise the plain "Added to list" */
    function showAdded(n, where) {
      confirmState.n += n || 1;
      clearTimeout(confirmState.t);
      confirmHeads().forEach((head) => {
        let strip = head.querySelector('.hconfirm');
        if (!strip) { head.insertAdjacentHTML('beforeend', HCONFIRM); strip = head.querySelector('.hconfirm'); }
        strip.querySelector('.hconfirm__n').textContent = confirmState.n > 1 ? confirmState.n + ' ' + T.itemsPlural : '';
        const label = strip.querySelector('.hconfirm__t');
        if (label) label.textContent = where ? T.addedShort + ' \u00b7 ' + where : T.addedToList;
        // remove + reflow + add restarts the CSS animation on a second add
        head.classList.remove('is-confirm'); void head.offsetWidth; head.classList.add('is-confirm');
      });
      confirmState.t = setTimeout(() => { confirmHeads().forEach((h) => h.classList.remove('is-confirm')); confirmState.n = 0; }, 2400);
    }
    /* ghosts: a removed line's snapshot + where it was; rendered by
       groupedLines in the bucket (room) it came from */
    let ghosts = [];
    let renderScope = 'd'; // 'd' drawer, 'b' board: set by each renderer before it builds
    function ghostHTML(g) {
      const seen = g.seen || (g.seen = {});
      const fresh = !seen[renderScope] && Date.now() - (g.removedAt || 0) < 300;
      seen[renderScope] = true;
      return '<div class="pline pline--undo' + (fresh ? ' is-fresh' : '') + '" data-pundo="' + esc(g.id) + '"><span><b>' + T.removed + '</b> \u00b7 ' + esc(g.line.title) + '</span>' +
        '<button type="button" data-pundo-btn="' + esc(g.id) + '">' + T.undo + '</button></div>';
    }
    function dropGhost(g) { ghosts = ghosts.filter((x) => x !== g); }
    /** an order whose LAST line was just removed still renders its line list
     *  (and a guest's board still renders its projects) while the Undo ghost
     *  lives — otherwise the empty state replaces the list and takes the
     *  Undo with it (seen on the #349 preview: removing the only line on the
     *  project page left no Undo to click) */
    function hasGhost(o) { return ghosts.some((g) => g.oid === o.id); }
    /* Removing a row: at once. Luke, 2026-09-17: "when I remove an item, the
       removed line with undo disappears and then reappears ... It should just
       immediately be there". The first version tinted and collapsed the row
       for 380 ms, THEN committed and rendered the ghost, and every later
       re-render replayed the ghost's fade-in — a blink. Now the line leaves
       and the ghost renders in the same frame; the ghost carries is-fresh
       only on its FIRST paint in each root (drawer, page), which is the only
       thing the theme animates (.pline--undo.is-fresh, 140 ms). */
    function removeLineAnimated(el, s, index) {
      const doc = store.read();
      const p = store.byId(doc, s.p.id); const o = p && store.orderById(p, s.o.id);
      const line = o && o.lines[index];
      if (!line) { paint(); renderAll(); return; }
      store.removeLine(o, index);
      store.save(doc);
      const g = { id: 'u_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6), pid: p.id, oid: o.id, index: index, line: line, removedAt: Date.now(), seen: {} };
      ghosts.push(g);
      setTimeout(() => { if (ghosts.indexOf(g) > -1) { dropGhost(g); renderAll(); } }, 5000);
      paint(); renderAll();
    }
    function undoRemove(id) {
      const g = ghosts.find((x) => x.id === id);
      if (!g) return;
      dropGhost(g);
      const doc = store.read();
      const p = store.byId(doc, g.pid); const o = p && store.orderById(p, g.oid);
      if (!o) { renderAll(); return; }
      store.restoreLine(o, g.index, g.line);
      store.save(doc); paint(); renderAll();
    }

    /* delete confirmation — soft-delete: projects AND orders archive, never
     * destroy. Pass an orderId to confirm an order archive instead. */
    let confirmEl = null;
    function closeConfirm() { if (confirmEl) { confirmEl.remove(); confirmEl = null; } }
    function openConfirm(projectId, orderId) {
      closeConfirm();
      const doc = store.read();
      const p = store.byId(doc, projectId);
      if (!p) return;
      const o = orderId ? store.orderById(p, orderId) : null;
      if (orderId && !o) return;
      const n = o ? o.lines.length : store.projectLines(p).length;
      const count = n + ' ' + (n === 1 ? T.items : T.itemsPlural);
      const title = o ? T.confirmTitleOrder : T.confirmTitle;
      const body = o
        ? T.confirmBodyOrder
            .replace('{name}', '<b>' + esc(store.orderName(o)) + '</b>')
            .replace('{count}', count)
            .replace('{project}', '<b>' + esc(p.name) + '</b>')
        : T.confirmBody
            .replace('{name}', '<b>' + esc(p.name) + '</b>')
            .replace('{count}', count);
      confirmEl = document.createElement('div');
      confirmEl.className = 'pconfirm';
      confirmEl.innerHTML = '<div class="pconfirm__scrim" data-pconfirm-cancel></div>' +
        '<div class="pconfirm__card" role="alertdialog" aria-modal="true">' +
        '<p class="pconfirm__title">' + title + '</p>' +
        '<p class="pconfirm__body">' + body + '</p>' +
        '<div class="pconfirm__actions">' +
        '<button type="button" class="btn btn--ghost" data-pconfirm-cancel>' + T.confirmNo + '</button>' +
        '<button type="button" class="btn pconfirm__yes" data-pconfirm-yes="' + p.id + '"' + (o ? ' data-pconfirm-order="' + o.id + '"' : '') + '>' + (o ? T.confirmYesOrder : T.confirmYes) + '</button>' +
        '</div></div>';
      document.body.appendChild(confirmEl);
      const yes = confirmEl.querySelector('[data-pconfirm-yes]');
      if (yes) yes.focus();
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-pconfirm-cancel]')) { closeConfirm(); return; }
      const yes = e.target.closest('[data-pconfirm-yes]');
      if (yes) {
        const doc = store.read();
        if (yes.dataset.pconfirmOrder) store.archiveOrder(doc, yes.dataset.pconfirmYes, yes.dataset.pconfirmOrder);
        else store.archiveProject(doc, yes.dataset.pconfirmYes);
        store.save(doc);
        closeConfirm();
        paint(); renderAll(); comboRefocus('project');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && confirmEl) { e.stopPropagation(); closeConfirm(); }
    }, true);
    let comboArmedDel = null;
    function customerOptions(doc) {
      const counts = {};
      doc.projects.forEach((p) => {
        const c = (p.customerRef || '').trim();
        if (c) counts[c] = (counts[c] || 0) + 1;
      });
      return Object.keys(counts).sort((a, b) => counts[b] - counts[a]).map((name) => ({ name: name, count: counts[name] }));
    }
    function comboRowProject(x, activeId) {
      const pls = store.projectLines(x);
      const n = pls.length;
      const est = store.estimate(pls);
      return '<div class="pcombo__row' + (x.id === activeId ? ' is-active' : '') + (comboArmedDel === x.id ? ' is-armed' : '') + '" data-pcombo-row="' + x.id + '">' +
        '<span class="pcombo__icon pcombo__editbtn" data-pcombo-edit="' + x.id + '" role="button" tabindex="0" title="Rename project">' + IC.pencil + '</span>' +
        '<button type="button" class="pcombo__opt" role="option" data-pcombo-opt="' + x.id + '">' +
        '<span class="pcombo__name">' + esc(x.name) + '</span>' +
        '<span class="pcombo__meta">' + n + ' ' + (n === 1 ? T.items : T.itemsPlural) + (est ? ' \u00b7 ' + money(est) : '') + '</span>' +
        '<span class="pcombo__tick" aria-hidden="true">' + IC.tick + '</span>' +
        '</button>' +
        '<span class="pcombo__icon pcombo__delbtn" data-pcombo-del="' + x.id + '" role="button" tabindex="0" title="' + (comboArmedDel === x.id ? T.confirmDelete : T.deleteProject) + '">' + IC.trash + '</span>' +
        '</div>';
    }
    function comboRowCustomer(x, current) {
      return '<div class="pcombo__row' + (x.name === current ? ' is-active' : '') + '">' +
        '<button type="button" class="pcombo__opt" role="option" data-pcombo-opt="' + esc(x.name) + '">' +
        '<span class="pcombo__name">' + esc(x.name) + '</span>' +
        '<span class="pcombo__meta">' + x.count + ' ' + (x.count === 1 ? T.comboProject : T.comboProjects) + '</span>' +
        '<span class="pcombo__tick" aria-hidden="true">' + IC.tick + '</span>' +
        '</button></div>';
    }
    function comboHTML(kind, value, placeholder) {
      return '<div class="pcombo" data-pcombo="' + kind + '">' +
        '<input class="pcombo__input" data-pcombo-input type="text" role="combobox" aria-expanded="false" ' +
        'aria-label="' + (kind === 'project' ? T.metaName : kind === 'room' ? T.roomPickTitle : T.metaCustomer) + '" placeholder="' + placeholder + '" value="' + esc(value) + '" autocomplete="off" spellcheck="false">' +
        '<svg class="pcombo__caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9.5 6 6 6-6"/></svg>' +
        '<div class="pcombo__panel" role="listbox" data-pcombo-panel hidden></div>' +
        '</div>';
    }
    function comboPanel(kind, query) {
      const doc = store.read();
      const q = (query || '').trim().toLowerCase();
      let html = '';
      let exact = false;
      if (kind === 'room') {
        const r = roomPanel(doc, q);
        html = r.html; exact = r.exact;
      } else if (kind === 'project') {
        const open = store.activeProjects(doc);
        const matches = open.filter((x) => !q || x.name.toLowerCase().includes(q));
        html = matches.map((x) => comboRowProject(x, doc.activeProjectId)).join('');
        exact = open.some((x) => x.name.toLowerCase() === q);
      } else {
        const current = (store.active(doc).customerRef || '').trim();
        const matches = customerOptions(doc).filter((x) => !q || x.name.toLowerCase().includes(q));
        html = matches.map((x) => comboRowCustomer(x, current)).join('');
        exact = customerOptions(doc).some((x) => x.name.toLowerCase() === q);
      }
      if (q && !exact) {
        html += '<button type="button" class="pcombo__create" data-pcombo-create data-name="' + esc(query.trim()) + '">' +
          (kind === 'room' ? T.roomCreate : T.comboCreate) + ' \u201C<b>' + esc(query.trim()) + '</b>\u201D</button>';
      }
      if (!html) html = '<p class="pcombo__none">' + (kind === 'project' ? T.comboNone : kind === 'room' ? T.roomNone : T.comboNoneCustomer) + '</p>';
      return html;
    }
    function comboEls(from) {
      const root = from.closest('[data-pcombo]');
      return root ? { root: root, kind: root.dataset.pcombo, input: root.querySelector('[data-pcombo-input]'), panel: root.querySelector('[data-pcombo-panel]') } : null;
    }
    function comboOpen(c, query) {
      c.panel.innerHTML = comboPanel(c.kind, query);
      c.panel.hidden = false;
      c.input.setAttribute('aria-expanded', 'true');
      c.root.classList.add('is-open');
    }
    function comboClose(c, restore) {
      if (c.kind === 'room') { closeRoomPicker(true); return; } // the picker IS its combo
      comboArmedDel = null;
      c.panel.hidden = true;
      c.input.setAttribute('aria-expanded', 'false');
      c.root.classList.remove('is-open');
      if (restore) {
        const doc = store.read();
        c.input.value = c.kind === 'project' ? store.active(doc).name : (store.active(doc).customerRef || '');
      }
    }
    function comboCommit(kind, pickValue, isCreate) {
      if (kind === 'room') { commitRoomPick(pickValue, isCreate); return; }
      const doc = store.read();
      if (kind === 'project') {
        if (isCreate) store.addProject(doc, pickValue);
        else doc.activeProjectId = pickValue;
      } else {
        store.active(doc).customerRef = pickValue;
      }
      store.save(doc); paint(); renderAll();
    }
    function comboRefocus(kind) {
      const inp = document.querySelector('[data-pcombo="' + kind + '"] [data-pcombo-input]');
      if (inp) inp.focus();
    }
    document.addEventListener('focusin', (e) => {
      if (e.target.hasAttribute && e.target.hasAttribute('data-pcombo-input')) {
        const c = comboEls(e.target);
        if (c) { comboOpen(c, ''); e.target.select(); }
      }
    });
    document.addEventListener('input', (e) => {
      if (e.target.hasAttribute && e.target.hasAttribute('data-pcombo-input')) {
        const c = comboEls(e.target);
        if (c) comboOpen(c, e.target.value);
      }
    });
    // capture phase: stopPropagation must beat host bubble listeners on
    // document (drawer Esc-close) — bubble-phase stopPropagation can't
    document.addEventListener('keydown', (e) => {
      // inline rename field
      if (e.target.hasAttribute && e.target.hasAttribute('data-pcombo-rename')) {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.stopPropagation(); e.target.dataset.cancel = '1'; e.target.blur(); }
        return;
      }
      if (!e.target.hasAttribute || !e.target.hasAttribute('data-pcombo-input')) return;
      const c = comboEls(e.target);
      if (!c) return;
      if (e.key === 'Escape') {
        // layered dismissal: an open panel swallows Esc; a closed one lets the
        // host's own Esc handling (e.g. drawer close) see the event
        if (!c.panel.hidden) { comboClose(c, true); e.stopPropagation(); }
        return;
      }
      const opts = [...c.panel.querySelectorAll('[data-pcombo-opt],[data-pcombo-create]')];
      const hot = c.panel.querySelector('.is-hot');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (c.panel.hidden) { comboOpen(c, e.target.value); return; }
        let i = opts.indexOf(hot);
        i = e.key === 'ArrowDown' ? Math.min(opts.length - 1, i + 1) : Math.max(0, i - 1);
        opts.forEach((o) => o.classList.remove('is-hot'));
        if (opts[i]) { opts[i].classList.add('is-hot'); opts[i].scrollIntoView({ block: 'nearest' }); }
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const pick = hot || opts[0];
        if (!pick) return;
        if (pick.hasAttribute('data-pcombo-create')) comboCommit(c.kind, pick.dataset.name, true);
        else comboCommit(c.kind, pick.dataset.pcomboOpt, false);
        return;
      }
    }, true);
    document.addEventListener('focusout', (e) => {
      if (!e.target.hasAttribute || !e.target.hasAttribute('data-pcombo-rename')) return;
      const id = e.target.dataset.pcomboRename;
      const cancelled = e.target.dataset.cancel === '1';
      const name = e.target.value.trim();
      if (!cancelled && name) {
        const doc = store.read();
        const p = store.byId(doc, id);
        if (p) { p.name = name; store.save(doc); }
      }
      paint(); renderAll(); comboRefocus('project');
    });
    document.addEventListener('pointerdown', (e) => {
      if (e.target.closest && e.target.closest('.pconfirm')) return;
      document.querySelectorAll('[data-pcombo].is-open').forEach((root) => {
        if (!root.contains(e.target)) {
          comboClose({ root: root, kind: root.dataset.pcombo, input: root.querySelector('[data-pcombo-input]'), panel: root.querySelector('[data-pcombo-panel]') }, true);
        }
      });
    });

    /* ---------- drawer ---------- */
    /** one order section: slim header (seq, name, count/subtotal) that is the
     *  accordion toggle AND the active-order switch; the active order expands
     *  with its meta row (label / PO / quote) and lines */
    /** yyyy-mm-dd -> "14 Nov" in the shop locale; '' when unset or unparsable */
    function shortDate(iso) {
      if (!iso) return '';
      const d = new Date(iso + 'T00:00:00');
      if (isNaN(d.getTime())) return '';
      try { return d.toLocaleDateString(cfg.locale, { day: 'numeric', month: 'short' }); } catch (e) { return iso; }
    }
    /** one order: the compact-cart band language (Luke, 2026-09-17, approved
     *  from the mock). The HEAD BLOCK is one tinted, sticky block — title row
     *  (seq, name, items · value, PO / Ready chips, "saving here") and the
     *  fields row (name, PO #, quote #, ready by, delete) — so the subdivision
     *  reads at a glance like the cart's Bulky items band. Every open order is
     *  expanded (rooms are the sub-dividers inside it); clicking the title row
     *  still makes it the order new saves go to. The per-order add button
     *  renders only when the project holds MORE than one order — with one,
     *  the footer button is that order's. */
    /** the order's own fields — label, PO #, Quote #, Ready by, delete —
     *  one block for the drawer band and the project page (parity, 2026-09-17) */
    function orderMetaHTML(o) {
      return '<div class="porder__meta">' +
        '<label class="pdrawer__meta-field"><span>' + T.orderFallback + ' ' + o.seq + '</span><input type="text" data-pometa="label" value="' + esc(o.label) + '" placeholder="' + T.orderLabelPh + '" maxlength="60"></label>' +
        '<label class="pdrawer__meta-field"><span>' + T.metaPo + '</span><input type="text" data-pometa="poNumber" value="' + esc(o.poNumber) + '" placeholder="' + T.metaPoPh + '"></label>' +
        '<label class="pdrawer__meta-field"><span>' + T.metaQuote + '</span><input type="text" data-pometa="quoteNumber" value="' + esc(o.quoteNumber) + '" placeholder="' + T.metaQuotePh + '"></label>' +
        '<label class="pdrawer__meta-field pdrawer__meta-field--date" title="' + esc(T.readyTip) + '"><span>' + T.metaReady + '</span><input type="date" data-pometa="readyBy" value="' + esc(o.readyBy || '') + '"></label>' +
        '<span class="pcombo__icon porder__del" data-porder-del="' + o.id + '" role="button" tabindex="0" title="' + T.deleteOrder + '">' + IC.trash + '</span>' +
        '</div>';
    }
    function orderSection(p, o, isActive, multi) {
      const n = o.lines.length;
      const est = store.estimate(o.lines);
      const ref = store.orderRef(p, o);
      const ready = shortDate(o.readyBy);
      let html = '<section class="porder porder--band' + (isActive ? ' is-active' : '') + '" data-porder-sec="' + o.id + '">';
      html += '<div class="porder__block">' +
        '<button type="button" class="porder__head" data-porder-activate="' + o.id + '">' +
        '<span class="porder__seq">' + o.seq + '</span>' +
        '<span class="porder__name">' + esc(store.orderName(o)) + (ref ? '<small class="porder__ref">' + esc(ref) + '</small>' : '') + '</span>' +
        (isActive ? '<span class="porder__here">' + T.savingHere + '</span>' : '') +
        '<span class="porder__sum"><b>' + n + ' ' + (n === 1 ? T.items : T.itemsPlural) + '</b>' + (est ? ' \u00b7 ' + rollHTML('o:' + o.id, money(est)) : '') +
        (o.poNumber ? '<span class="porder__chip">' + esc(T.poChip.replace('{po}', o.poNumber)) + '</span>' : '') +
        (ready ? '<span class="porder__chip">' + esc(T.readyChip.replace('{date}', ready)) + '</span>' : '') + '</span>' +
        '</button>' +
        orderMetaHTML(o) + '</div>';
      html += '<div class="porder__body">' +
        (o.lines.length || store.groupsOf(o).length || hasGhost(o)
          ? '<div class="pdrawer__lines pdrawer__lines--grouped" data-porder="' + o.id + '">' + groupedLines(o) + '</div>' + roomAdd(o) +
            (multi && o.lines.length ? '<div class="porder__cta"><button type="button" class="btn btn--ghost porder__addbtn" data-porder-add="' + o.id + '">' + T.addOrderThis + '</button></div>' : '')
          : '<p class="porder__empty" data-pgroup-drop="" data-porder="' + o.id + '">' + T.emptyDrawer + '</p>') +
        '</div>';
      return html + '</section>';
    }
    /* The Lists markup, ONE builder for the drawer and the project page
       (Luke, 2026-09-17: "The cart page and project/list page should reflect
       the same design styling and decisions as the cart and list cart
       drawers. The pages should be updated to match the drawers exactly.").
       `page` only swaps what cannot be the same: Continue shopping is a
       link (the drawer's closes the drawer) and there is no "View all"
       (the page IS all). */
    function listHTML(doc, page) {
      const p = store.active(doc);
      const active = store.activeOrder(doc, p);
      const open = store.openOrders(p);
      const pls = store.projectLines(p);
      const multi = open.length > 1;
      let html = '';
      const continueBtn = page
        ? '<a class="btn btn--ghost" href="' + esc(cfg.continueUrl) + '">' + T.continueShopping + '</a>'
        : '<button class="btn btn--ghost" type="button" data-flyout-close>' + T.continueShopping + '</button>';
      const viewAll = page ? '' : '<a class="pdrawer__all" href="' + cfg.boardUrl + '">' + T.viewAll + '</a>';
      html += authNudge();
      html += '<div class="pdrawer__proj">' +
        '<label class="pdrawer__meta-field pdrawer__meta-field--combo"><span>' + T.metaName + '</span>' + comboHTML('project', p.name, T.comboPlaceholder) + '</label>' +
        metaGrid(p, { noName: true, customerCombo: true }) + '</div>';
      html += '<div class="porders" data-porders>' + open.map((o) => orderSection(p, o, o.id === active.id, multi)).join('') +
        '<button type="button" class="porder__new" data-porder-new>' + T.newOrder + '</button></div>';
      /* Chat (presence-gated on Inbox, paintDrawerChrome) and Share (only
         once the projects app can serve a share) — the same two buttons in
         the empty and the full footer */
      const chatBtn = '<button type="button" class="btn btn--ghost pdrawer__chat" data-pchat data-tip="' + esc(T.chatTip) + '"' + (inboxKnown() ? '' : ' hidden') + '>' + IC.chat + '<span>' + T.chat + '</span></button>';
      const shareBtn = cfg.shareBase && loggedIn ? '<button type="button" class="btn btn--ghost btn--icon pdrawer__share" data-pshare aria-label="' + esc(T.share) + '" data-tip="' + esc(T.shareTip) + '">' + IC.share + '</button>' : '';
      /* The footer goes OUTSIDE the scroller when the header provides the
         slot ([data-project-foot], a sibling of the body — theme #349), so
         it is pinned to the panel's bottom edge whatever the list length,
         exactly as the cart drawer keeps its footer beside its scroller.
         Luke, 2026-09-17: "the buttons should all be in the footer just
         like the design for the cart, this is not correct." Without the
         slot (older header markup) it renders inside the body as before. */
      let foot = '';
      if (!pls.length) {
        /* Empty list (however many open orders it has — with two empty orders
           the old `open.length === 1` guard rendered NO footer at all, seen on
           the #349 preview): the SAME footer as a full one, minus the total row and
           the primary add (nothing to add) — Luke, 2026-09-17: "Continue
           shopping should be in a footer section just like add to cart,
           chat, share, etc. Let's keep the design language consistent."
           It was a lone centred button (flyout__empty--slim) before. */
        foot = '<div class="flyout__foot flyout__foot--compact flyout__foot--empty">' +
          '<div class="pdrawer__ctas pdrawer__ctas--empty">' + chatBtn + shareBtn +
          continueBtn + '</div>' + viewAll +
          '</div>';
      } else if (pls.length) {
        const total = store.estimate(pls);
        const rooms = [];
        open.forEach((o) => store.groupsOf(o).forEach((g) => { if (g.label && rooms.indexOf(g.label) < 0) rooms.push(g.label); }));
        const units = pls.reduce((a, l) => a + (l.qty || 1), 0);
        const crumb = [open.length + ' ' + (open.length === 1 ? T.ordersWord : T.ordersPlural), units + ' ' + (units === 1 ? T.items : T.itemsPlural)]
          .concat(rooms.length ? [rooms.join(', ')] : []).join(' \u00b7 ');
        /* footer in the compact cart's shape: one total row with the crumb
           inline, then Chat (presence-gated on Inbox), Share (only once the
           projects app can serve a share), Continue shopping and the primary:
           with one order it adds THAT order; with several it adds them all
           (Luke, 2026-09-17). */
        const primary = multi
          ? '<button class="btn" type="button" data-project-addall>' + T.addAllOrders + '</button>'
          : '<button class="btn" type="button" data-porder-add="' + open[0].id + '">' + esc(T.addOrderNamed.replace('{order}', store.orderName(open[0]))) + '</button>';
        foot = '<div class="flyout__foot flyout__foot--compact">' +
          '<div class="pdrawer__sum"><span><span class="pdrawer__sum-lbl">' + (multi ? T.projectTotal : T.estimated) + '</span><small class="pdrawer__crumb" title="' + esc(crumb) + '">' + esc(crumb) + '</small></span><b>' + rollHTML('p:' + p.id, money(total)) + '</b></div>' +
          '<div class="pdrawer__ctas">' + chatBtn + shareBtn +
          continueBtn +
          primary +
          '</div>' + viewAll +
          '</div>';
      }
      return { html: html, foot: foot };
    }
    function render() {
      const root = document.querySelector('[data-project-root]');
      if (!root) return;
      renderScope = 'd';
      const built = listHTML(store.read(), false);
      const html = built.html; const foot = built.foot;
      const slot = document.querySelector('[data-project-foot]');
      const prev = Object.assign(rollSnapshot(root), slot ? rollSnapshot(slot) : {});
      let footKept = false;
      if (slot) {
        root.innerHTML = html;
        /* The footer's DOM is REPLACED only when its structure changes (a
           button appears, the primary's label changes). A qty change or an
           add only changes numbers — the total and the crumb — and those
           are patched in place: recreating the CTA row on every change made
           the docked Chat button blink and Inbox's floating launcher flash
           behind it (Luke, 2026-09-17: "every time we add something to the
           project or change the QTY ... the free floating Chat button
           appears and disappears"). */
        const sig = footSignature(foot);
        if (slot.firstChild && slot.dataset.sig === sig) { patchFoot(slot, foot); footKept = true; }
        else { slot.innerHTML = foot; slot.dataset.sig = sig; }
      } else root.innerHTML = html + foot;
      paintDrawerChrome(root);
      if (slot && !footKept) paintDrawerChrome(slot);
      rollPaint(root, prev);
      if (slot && !footKept) rollPaint(slot, prev);
    }
    /** the footer's HTML with its changing figures blanked: equal signatures
     *  mean "same buttons, same labels, only numbers differ" */
    function footSignature(foot) {
      return foot
        .replace(/(<span class="roll" data-roll="[^"]*">)[^<]*(<\/span>)/g, '$1$2')
        .replace(/(<small class="pdrawer__crumb" title=")[^"]*(">)[^<]*(<\/small>)/g, '$1$2$3');
    }
    function patchFoot(slot, foot) {
      const tmp = document.createElement('div');
      tmp.innerHTML = foot;
      const api = rollApi();
      tmp.querySelectorAll('[data-roll]').forEach((n) => {
        const el = slot.querySelector('[data-roll="' + n.dataset.roll.replace(/"/g, '\\"') + '"]');
        if (!el) return;
        if (api && !REDUCED) api.set(el, n.textContent);
        else if (api) api.mount(el, n.textContent);
        else el.textContent = n.textContent;
      });
      const nc = tmp.querySelector('.pdrawer__crumb'); const oc = slot.querySelector('.pdrawer__crumb');
      if (nc && oc) { if (oc.textContent !== nc.textContent) oc.textContent = nc.textContent; oc.title = nc.title; }
    }
    /* The docked Chat button must never blink. Whether Inbox exists is
       cached the moment a <shopify-chat> is seen (chat.js is async) and the
       button renders un-hidden from then on; until then one observer waits
       for Inbox and un-hides the buttons already on screen. Hiding Inbox's
       own floating launcher while a drawer is open is the theme's job
       (theme #353 keys it on the open panel, not on this button).
       body.lists-open mirrors the panel's hidden attribute for the theme. */
    let inboxSeen = false;
    function inboxKnown() {
      if (!inboxSeen && document.querySelector('shopify-chat')) inboxSeen = true;
      return inboxSeen;
    }
    function paintListsOpen() {
      const panel = document.querySelector('[data-flyout="project"]');
      document.body.classList.toggle('lists-open', !!(panel && !panel.hidden));
    }
    (function watchListsChrome() {
      const panel = document.querySelector('[data-flyout="project"]');
      if (!global.MutationObserver) return;
      if (panel) { new MutationObserver(paintListsOpen).observe(panel, { attributes: true, attributeFilter: ['hidden'] }); paintListsOpen(); }
      if (inboxKnown()) return;
      const mo = new MutationObserver(() => {
        if (!inboxKnown()) return;
        mo.disconnect();
        document.querySelectorAll('[data-pchat]').forEach((b) => { b.hidden = false; });
      });
      mo.observe(document.body, { childList: true });
    })();
    /* the sticky room sub-dividers pin under the order head block, whose
       height depends on the fields row wrapping — measure it once per render
       and hand it to the CSS; and the Chat button shows only when Inbox has
       mounted its <shopify-chat> */
    function paintDrawerChrome(root) {
      const block = root.querySelector('.porder__block');
      const list = root.querySelector('[data-porders]');
      if (block && list) list.style.setProperty('--porder-h', Math.round(block.getBoundingClientRect().height) + 'px');
      const chat = root.querySelector('[data-pchat]');
      if (chat && chat.hidden === inboxKnown()) chat.hidden = !inboxKnown();
    }

    /* ---------- the project page: the drawer, in a column ----------
       The old board (project cards, a Projects/Customers switch, a customers
       view, its own order heads, the guest gate card) is gone: the page renders
       listHTML — the drawer's body and footer — inside .plist--page, which the
       theme dresses with the drawer's skin at the drawer's width. Guests see
       the drawer's sign-in strip, not a gate card. */
    function renderBoard() {
      const board = document.querySelector('[data-project-board]');
      if (!board) return;
      const prev = rollSnapshot(board);
      paintBoard(board);
      rollPaint(board, prev);
    }
    function paintBoard(board) {
      renderScope = 'b';
      board.classList.remove('is-guest');
      const built = listHTML(store.read(), true);
      board.innerHTML = '<div class="plist plist--page">' +
        '<div class="plist__body">' + built.html + '</div>' +
        '<div class="plist__foot">' + built.foot + '</div></div>';
      paintDrawerChrome(board);
    }
    /** a typed quantity keeps its focus, caret and draft across ANY render —
     *  the details backfill and a sync adopt render a moment after a
     *  keystroke and used to take the field away mid-typing (seen on the
     *  theme #368 preview: the second digit of "12" was lost) */
    function keepTypingFocus() {
      const a = document.activeElement;
      if (!a || !a.hasAttribute || !a.hasAttribute('data-pline-qtyin')) return null;
      const row = a.closest('[data-pline-id]');
      if (!row) return null;
      return { lid: row.dataset.plineId, inBoard: !!a.closest('[data-project-board]'), value: a.value };
    }
    /** focus a re-rendered quantity field with the caret at the END. A
     *  number input refuses setSelectionRange and focus() lands the caret at
     *  the start, so the next digit went in FRONT of the first ("12" typed
     *  became 21 on the theme #368 preview): flip to text for the caret move,
     *  then back. */
    function focusQtyAtEnd(el) {
      try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
      const n = el.value.length;
      try { el.setSelectionRange(n, n); return; } catch (err) { /* number input */ }
      try { el.type = 'text'; el.setSelectionRange(n, n); el.type = 'number'; } catch (err) { /* leave it */ }
    }
    function restoreTypingFocus(keep) {
      if (!keep) return;
      const scope = document.querySelector(keep.inBoard ? '[data-project-board]' : '[data-project-root]');
      const el = scope && scope.querySelector('[data-pline-id="' + keep.lid + '"] [data-pline-qtyin]');
      if (!el) return;
      if (keep.value === '') el.value = ''; // an empty draft stays empty until blur
      focusQtyAtEnd(el);
    }
    function renderAll() {
      if (lineDrag) { pendingRender = true; return; } // never re-render under a live drag
      const keep = keepTypingFocus();
      render(); renderBoard(); restoreTypingFocus(keep); emitChange(); backfillDetails();
      if (roomPick) { const b = roomBadgeOf(roomPick); if (b) { roomPick.badge = b; b.setAttribute('aria-expanded', 'true'); placeRoomPicker(); } else closeRoomPicker(false); }
    }

    /* ---------- room picker (the badge's pick-or-create) ----------
       Luke, 2026-09-17: "let's make a quick create room possible from the
       badge with a properly styled dropdown with picker or create new,
       similar to customer field and project field in the list cart already."
       So it IS that component: comboHTML/comboPanel with a `room` kind, in a
       small floating card anchored to the badge (flipped up near the bottom
       edge, kept inside the viewport). Options: "No room" and the line's own
       order's rooms first, then the other open orders' rooms under their
       order name; a name that does not exist offers "Create room '<typed>'",
       which mints it in the line's order and assigns the line in one step.
       Rooms need an account (RFC §4): signed out, the card is one quiet line
       with a sign-in link, never a dead menu. */
    let roomPick = null;
    let roomPickClosed = { lid: null, at: 0 };
    let roomPickOpenedAt = 0;
    function roomPanel(doc, q) {
      if (!roomPick) return { html: '', exact: false };
      const p = store.byId(doc, roomPick.pid);
      const cur = p && store.orderById(p, roomPick.oid);
      const line = cur && store.lineById(cur, roomPick.lid);
      if (!line) return { html: '', exact: false };
      const row = (o, g) => {
        const gid = g ? g.id : '';
        const name = g ? (g.label || T.roomLabel) : T.noRoom;
        if (q && name.toLowerCase().indexOf(q) < 0) return '';
        const n = o.lines.filter((l) => (l.groupId || '') === gid).length;
        const on = o === cur && (line.groupId || '') === gid;
        return '<div class="pcombo__row' + (on ? ' is-active' : '') + '">' +
          '<button type="button" class="pcombo__opt" role="option" aria-selected="' + on + '" data-pcombo-opt="' + esc(o.id + '|' + gid) + '">' +
          '<span class="pcombo__name">' + esc(name) + '</span>' +
          '<span class="pcombo__meta">' + n + ' ' + (n === 1 ? T.items : T.itemsPlural) + '</span>' +
          '<span class="pcombo__tick" aria-hidden="true">' + IC.tick + '</span></button></div>';
      };
      const block = (o) => row(o, null) + store.groupsOf(o).map((g) => row(o, g)).join('');
      let html = block(cur);
      store.openOrders(p).filter((o) => o !== cur).forEach((o) => {
        const rows = block(o);
        if (rows) html += '<p class="pcombo__group">' + esc(store.orderName(o)) + '</p>' + rows;
      });
      const exact = store.groupsOf(cur).some((g) => (g.label || '').toLowerCase() === q);
      return { html: html, exact: exact };
    }
    function placeRoomPicker() {
      if (!roomPick) return;
      const el = roomPick.el; const b = roomPick.badge.getBoundingClientRect();
      const w = Math.min(280, innerWidth - 16);
      el.style.width = w + 'px';
      const h = el.offsetHeight || 220;
      const left = Math.min(Math.max(8, b.left), innerWidth - w - 8);
      let top = b.bottom + 6;
      if (top + h > innerHeight - 8) top = Math.max(8, b.top - 6 - h); // flip up near the bottom edge
      el.style.left = left + 'px'; el.style.top = top + 'px';
    }
    function closeRoomPicker(refocus) {
      if (!roomPick) return;
      const rp = roomPick; roomPick = null;
      roomPickClosed = { lid: rp.lid, at: Date.now() };
      rp.el.remove();
      const badge = document.contains(rp.badge) ? rp.badge : null;
      if (badge) { badge.setAttribute('aria-expanded', 'false'); if (refocus) badge.focus(); }
    }
    function roomBadgeOf(rp) {
      const scope = document.querySelector(rp.inBoard ? '[data-project-board]' : '[data-project-root]');
      return scope ? scope.querySelector('[data-pline-id="' + rp.lid + '"] [data-pline-room]') : null;
    }
    function openRoomPicker(badge) {
      const s = orderScope(badge);
      const row = badge.closest('[data-pline]');
      const l = row && s.o.lines[Number(row.dataset.pline)];
      if (!l) return;
      // the pointerdown that closed the open picker is followed by this click: a toggle, not a reopen
      if (roomPickClosed.lid === l.id && Date.now() - roomPickClosed.at < 350) return;
      closeRoomPicker(false);
      const el = document.createElement('div');
      el.className = 'proompick';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-label', T.roomPickTitle);
      roomPick = { el: el, pid: s.p.id, oid: s.o.id, lid: l.id, badge: badge, inBoard: !!badge.closest('[data-project-board]') };
      el.innerHTML = loggedIn
        ? '<p class="psave__mini">' + T.roomPickTitle + ' <small>' + esc(l.title) + '</small></p>' + comboHTML('room', '', T.roomPickPh)
        : '<p class="psave__roomgate">' + T.roomsGate + ' <a href="' + esc(loginHref()) + '">' + T.signIn + '</a> ' + T.roomsGateLink + '.</p>';
      document.body.appendChild(el);
      badge.setAttribute('aria-expanded', 'true');
      placeRoomPicker();
      roomPickOpenedAt = Date.now();
      const focusEl = el.querySelector('[data-pcombo-input]') || el.querySelector('a');
      /* preventScroll: focusing the field scrolled the page to it, and that
         scroll closed the picker in the same tick (seen on the #360 preview:
         the badge opened and shut before a key could be typed) */
      if (focusEl) { try { focusEl.focus({ preventScroll: true }); } catch (err) { focusEl.focus(); } }
      placeRoomPicker();
    }
    function commitRoomPick(value, isCreate) {
      if (!roomPick) return;
      const rp = roomPick;
      const doc = store.read();
      if (isCreate) store.createRoomFor(doc, rp.pid, rp.oid, rp.lid, value);
      else { const parts = String(value).split('|'); store.moveLineTo(doc, rp.pid, rp.oid, rp.lid, parts[0], parts[1] || null); }
      store.save(doc);
      closeRoomPicker(false);
      paint(); renderAll();
      const again = roomBadgeOf(rp);
      if (again) again.focus();
    }
    // the card follows nothing: a scroll or a resize under it closes it
    addEventListener('resize', () => closeRoomPicker(false));
    document.addEventListener('scroll', (e) => {
      if (!roomPick || Date.now() - roomPickOpenedAt < 400) return; // our own open, not the shopper scrolling away
      if (e.target && e.target.closest && e.target.closest('.proompick')) return;
      closeRoomPicker(false);
    }, true);
    document.addEventListener('pointerdown', (e) => {
      if (roomPick && !(e.target.closest && e.target.closest('.proompick'))) closeRoomPicker(false);
    });

    /* ---------- line drag and drop (pointer events) ----------
       Luke, 2026-09-17: "dragging a product into a room, and dragging in
       between orders and their rooms needs some polish, it's not smooth and
       feels a bit janky". It was native HTML5 drag and drop: a browser ghost
       image, no placeholder, targets that lit only on exact hover, no touch
       at all, and an innerHTML re-render on every drop. Now the grip starts
       a pointer-driven drag (mouse: past a 4px slop; touch and pen: a 250 ms
       long-press, so a swipe still scrolls — and a swipe that STARTS on the
       grip scrolls too, by hand, because the grip is touch-action:none):
       - the row lifts (position:fixed at its own rect, transform per frame,
         no layout per move) and leaves a placeholder of its height;
       - every valid target lights as a group — each room, "No room", each
         other order (rooms and empty orders alike) and the "+ Add room"
         block as a new-room drop zone (signed in) — and the one under the
         pointer gets the stronger state;
       - the placeholder moves to where the row would land and the siblings
         make room with a FLIP transform;
       - the scroller auto-scrolls near its top and bottom edges;
       - Escape, pointercancel or a drop outside animates the row home;
       - on drop the row settles onto the placeholder (180 ms) and only THEN
         does the state commit and the list re-render. renderAll is held
         while a drag is live (pendingRender).
       A move is not a removal: no Undo ghost. Totals roll on the re-render.
       Reduced motion: no lift, no FLIP, no settle. */
    let linePend = null;
    let lineDrag = null;
    let pendingRender = false;
    const HOLD_MS = 250; const TOUCH_SLOP = 8; const MOUSE_SLOP = 4; const EDGE = 56;
    const dragRootOf = (el) => el.closest('[data-pproj]') || el.closest('[data-project-board]') || el.closest('[data-project-root]');
    function scrollByHand(from, dy) {
      const sc = from.closest('[data-project-root]');
      if (sc) sc.scrollTop -= dy; else global.scrollBy(0, -dy);
    }
    function flipRows(root, mutate) {
      if (REDUCED) { mutate(); return; }
      const rows = [].slice.call(root.querySelectorAll('.pline:not(.is-lifted)'));
      const first = rows.map((r) => r.getBoundingClientRect());
      mutate();
      rows.forEach((r, i) => {
        const now = r.getBoundingClientRect();
        const dx = first[i].left - now.left; const dy = first[i].top - now.top;
        if (!dx && !dy) return;
        r.style.transition = 'none';
        r.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
        requestAnimationFrame(() => { r.style.transition = 'transform .15s ease'; r.style.transform = ''; });
      });
    }
    function beginLineDrag(pend) {
      const row = pend.row;
      const root = dragRootOf(row);
      if (!root) return;
      closeRoomPicker(false); closePopout();
      const s = orderScope(row);
      const rect = row.getBoundingClientRect();
      const ph = document.createElement('div');
      ph.className = 'pline-placeholder';
      ph.style.height = rect.height + 'px';
      row.parentNode.insertBefore(ph, row);
      row.classList.add('is-lifted');
      row.style.width = rect.width + 'px'; row.style.height = rect.height + 'px';
      row.style.position = 'fixed'; row.style.margin = '0';
      row.style.left = rect.left + 'px'; row.style.top = rect.top + 'px';
      // a transformed or contained ancestor would re-base position:fixed: measure and correct
      const r2 = row.getBoundingClientRect();
      row.style.left = (rect.left - (r2.left - rect.left)) + 'px';
      row.style.top = (rect.top - (r2.top - rect.top)) + 'px';
      const zones = [].slice.call(root.querySelectorAll('[data-pgroup-drop]' + (loggedIn ? ',[data-pgroup-newdrop]' : '')));
      zones.forEach((z) => z.classList.add('is-drag-target'));
      document.documentElement.classList.add('is-line-dragging');
      lineDrag = { row: row, ph: ph, rect: rect, root: root, zones: zones, pid: s.p.id, oid: s.o.id, lid: row.dataset.plineId,
        x: pend.x, y: pend.y, x0: pend.x, y0: pend.y, over: null, raf: 0, scroller: row.closest('[data-project-root]'), done: false };
      lineDrag.raf = requestAnimationFrame(dragFrame);
    }
    function placePlaceholder(zone, el) {
      const d = lineDrag;
      if (zone.tagName === 'P' || zone.hasAttribute('data-pgroup-newdrop')) return; // empty order / new room: highlight only
      if (el === d.ph) return;
      const under = el.closest && el.closest('.pline:not(.is-lifted):not(.pline--undo)');
      let before = null;
      if (under && under.parentNode === zone) {
        const r = under.getBoundingClientRect();
        const oneColumn = r.width > zone.getBoundingClientRect().width * 0.6;
        const after = oneColumn ? d.y > r.top + r.height / 2 : d.x > r.left + r.width / 2;
        before = after ? under.nextSibling : under;
      } else if (d.ph.parentNode === zone) return; // in the zone's own gaps: stay put
      if (before === d.ph) before = d.ph.nextSibling;
      if (d.ph.parentNode === zone && d.ph.nextSibling === before) return;
      flipRows(d.root, () => zone.insertBefore(d.ph, before));
    }
    function dragFrame() {
      const d = lineDrag;
      if (!d || d.done) return;
      d.row.style.transform = 'translate3d(' + (d.x - d.x0) + 'px,' + (d.y - d.y0) + 'px,0)' + (REDUCED ? '' : ' scale(1.02)');
      if (d.scroller) {
        const r = d.scroller.getBoundingClientRect();
        if (d.y < r.top + EDGE) d.scroller.scrollTop -= Math.ceil((r.top + EDGE - d.y) / 5);
        else if (d.y > r.bottom - EDGE) d.scroller.scrollTop += Math.ceil((d.y - (r.bottom - EDGE)) / 5);
      } else if (d.y < EDGE) global.scrollBy(0, -Math.ceil((EDGE - d.y) / 5));
      else if (d.y > innerHeight - EDGE) global.scrollBy(0, Math.ceil((d.y - (innerHeight - EDGE)) / 5));
      const el = document.elementFromPoint(d.x, d.y);
      let zone = el && el.closest ? el.closest('[data-pgroup-drop],[data-pgroup-newdrop]') : null;
      if (zone && d.zones.indexOf(zone) < 0) zone = null;
      if (zone !== d.over) {
        if (d.over) d.over.classList.remove('is-over');
        if (zone) zone.classList.add('is-over');
        d.over = zone;
      }
      if (zone && el) placePlaceholder(zone, el);
      d.raf = requestAnimationFrame(dragFrame);
    }
    function endLineDrag(commit) {
      const d = lineDrag;
      if (!d || d.done) return;
      d.done = true;
      cancelAnimationFrame(d.raf);
      const zone = commit ? d.over : null;
      let target = null;
      if (zone && zone.hasAttribute('data-pgroup-newdrop')) target = { newRoom: true, oid: zone.dataset.pgroupNewdrop };
      else if (zone) {
        const ow = zone.closest('[data-porder]');
        target = { oid: ow ? ow.dataset.porder : d.oid, gid: zone.dataset.pgroupDrop || null, before: null };
        if (d.ph.parentNode === zone) {
          let n = d.ph.nextSibling;
          while (n && !(n.nodeType === 1 && n.matches('.pline[data-pline-id]:not(.is-lifted)'))) n = n.nextSibling;
          target.before = n ? n.dataset.plineId : null;
        }
      }
      if (!target) d.row.parentNode.insertBefore(d.ph, d.row); // home
      const to = (target && (target.newRoom || d.ph.parentNode !== zone)) ? zone.getBoundingClientRect() : d.ph.getBoundingClientRect();
      const finish = () => {
        d.zones.forEach((z) => z.classList.remove('is-drag-target', 'is-over'));
        document.documentElement.classList.remove('is-line-dragging');
        d.ph.remove();
        d.row.classList.remove('is-lifted', 'is-settling');
        ['width', 'height', 'position', 'margin', 'left', 'top', 'transform'].forEach((k) => { d.row.style[k] = ''; });
        [].slice.call(d.root.querySelectorAll('.pline')).forEach((r) => { r.style.transition = ''; r.style.transform = ''; });
        // the root is rebuilt by the render below: remember how to find it again
        const rootSel = d.root.hasAttribute('data-pproj') ? '[data-pproj="' + d.root.dataset.pproj + '"]' : d.root.hasAttribute('data-project-board') ? '[data-project-board]' : '[data-project-root]';
        lineDrag = null;
        if (!target) { if (pendingRender) { pendingRender = false; renderAll(); } return; }
        pendingRender = false;
        const doc = store.read();
        let g = null;
        if (target.newRoom) {
          store.moveLineTo(doc, d.pid, d.oid, d.lid, target.oid, null);
          g = store.createRoomFor(doc, d.pid, target.oid, d.lid, '');
        } else store.moveLineTo(doc, d.pid, d.oid, d.lid, target.oid, target.gid, target.before);
        store.save(doc); paint(); renderAll();
        if (g) focusGroupLabel(g.id, document.querySelector(rootSel)); // name the new room straight away
      };
      if (REDUCED) { finish(); return; }
      d.row.classList.add('is-settling');
      d.row.style.transform = 'translate3d(' + (to.left - d.rect.left) + 'px,' + (to.top - d.rect.top) + 'px,0)';
      setTimeout(finish, 190);
    }
    document.addEventListener('pointerdown', (e) => {
      const grip = e.target.closest && e.target.closest('[data-pline-grip]');
      if (!grip || lineDrag || linePend || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const row = grip.closest('[data-pline]');
      if (!row) return;
      e.preventDefault();
      const pend = { grip: grip, row: row, id: e.pointerId, x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, touch: e.pointerType !== 'mouse', timer: null, scrolling: false };
      try { grip.setPointerCapture(e.pointerId); } catch (err) { /* synthetic pointers */ }
      if (pend.touch) pend.timer = setTimeout(() => { if (linePend === pend && !pend.scrolling) { grip.classList.add('is-held'); beginLineDrag(pend); } }, HOLD_MS);
      linePend = pend;
    });
    document.addEventListener('pointermove', (e) => {
      const pend = linePend;
      if (!pend || e.pointerId !== pend.id) return;
      if (lineDrag) { lineDrag.x = e.clientX; lineDrag.y = e.clientY; e.preventDefault(); return; }
      const dist = Math.hypot(e.clientX - pend.sx, e.clientY - pend.sy);
      if (pend.touch) {
        // a swipe that starts on the grip still scrolls: the grip is touch-action:none, so by hand
        if (!pend.scrolling && dist > TOUCH_SLOP) { pend.scrolling = true; clearTimeout(pend.timer); }
        if (pend.scrolling) scrollByHand(pend.row, e.clientY - pend.y);
        pend.x = e.clientX; pend.y = e.clientY;
      } else {
        pend.x = e.clientX; pend.y = e.clientY;
        if (dist > MOUSE_SLOP) { pend.x = pend.sx; pend.y = pend.sy; beginLineDrag(pend); if (lineDrag) { lineDrag.x = e.clientX; lineDrag.y = e.clientY; } }
      }
    }, { passive: false });
    function releaseLinePointer(e, commit) {
      const pend = linePend;
      if (!pend || (e && e.pointerId !== pend.id)) return;
      clearTimeout(pend.timer);
      pend.grip.classList.remove('is-held');
      try { pend.grip.releasePointerCapture(pend.id); } catch (err) { /* already gone */ }
      linePend = null;
      if (lineDrag) endLineDrag(commit);
    }
    document.addEventListener('pointerup', (e) => releaseLinePointer(e, true));
    document.addEventListener('pointercancel', (e) => releaseLinePointer(e, false));
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !lineDrag) return;
      e.stopPropagation(); e.preventDefault(); // Escape cancels the drag, not the drawer
      releaseLinePointer(null, false);
    }, true);
    // a long-press on the grip must not raise the context menu
    document.addEventListener('contextmenu', (e) => { if (e.target.closest && e.target.closest('[data-pline-grip]')) e.preventDefault(); });

    /* ---------- save popout ----------
       Three levels, progressive disclosure (Luke, 2026-09-17: "this widget
       needs more ability to not just add to a project, but also to add to a
       room and order"). Before this the popover only ever wrote to the
       project's ACTIVE order and never showed orders or rooms.
       1. Project row — one tap still saves to the active order with no
          room, exactly as before (and un-saves from wherever it is).
       2. The caret opens the project's open orders: picking one saves
          there (moving the line if it sits in another order — qty and note
          survive) and makes it the save target; "+ New order" mints one
          and saves into it.
       3. Under the orders, the rooms of the order that holds the product
          (else the save target): "No room", each room, "+ New room".
          Rooms need an account (RFC §4) — signed out, the level is one
          quiet line with a sign-in link, never a dead control.
       Keyboard: Up/Down walk every control, Right opens a project, Left
       closes it, Enter activates, Escape closes and returns focus to the
       trigger. */
    let pop = null;
    let popData = null;
    let popTrigger = null;
    let popOpenPid = null;
    let popRoomInput = false;
    function closePopout(returnFocus) {
      if (!pop) return;
      pop.remove(); pop = null; popData = null; popOpenPid = null; popRoomInput = false;
      // `=== true`: scroll/resize listeners call this with their event object
      if (returnFocus === true && popTrigger && popTrigger.focus) popTrigger.focus();
      popTrigger = null;
    }
    function roomLabel(o, gid) {
      const g = gid ? store.groupById(o, gid) : null;
      return g ? (g.label || T.roomLabel) : '';
    }
    /** "Order 2 · Ensuite" for a saved line */
    function whereText(at) {
      const room = roomLabel(at.order, at.line.groupId);
      return store.orderName(at.order) + (room ? ' · ' + room : '');
    }
    function popLevels(doc, p, at) {
      const target = store.activeOrder(doc, p);
      const roomOrder = at ? at.order : target;
      let html = '<div class="psave__levels" id="psave-lv-' + esc(p.id) + '" role="group" aria-label="' + esc(T.popChoose) + '" data-psave-pid="' + esc(p.id) + '">' +
        '<p class="psave__mini">' + T.popOrderHead + '</p>';
      html += store.openOrders(p).map((o) => {
        const on = !!(at && at.order === o);
        const est = store.estimate(o.lines);
        return '<button type="button" class="psave__orow' + (on ? ' is-on' : '') + '" data-psave-order="' + esc(o.id) + '" data-psave-nav="o:' + esc(o.id) + '" aria-pressed="' + on + '">' +
          '<span class="porder__seq">' + o.seq + '</span>' +
          '<span class="psave__oname">' + esc(store.orderName(o)) + '</span>' +
          (o.id === target.id ? '<span class="psave__here">' + T.savingHere + '</span>' : '') +
          '<span class="psave__count">' + o.lines.length + (est ? ' · ' + money(est) : '') + '</span>' +
          '<span class="psave__check' + (on ? ' is-on' : '') + '" aria-hidden="true"></span></button>';
      }).join('');
      html += '<button type="button" class="psave__onew" data-psave-neworder data-psave-nav="on:' + esc(p.id) + '">' + T.newOrder + '</button>';
      html += '<p class="psave__mini">' + T.popRoomHead + ' <small>' + T.popIn + ' ' + esc(store.orderName(roomOrder)) + '</small></p>';
      if (!loggedIn) {
        html += '<p class="psave__roomgate">' + T.roomsGate + ' <a href="' + esc(loginHref()) + '" data-psave-nav="rg:' + esc(p.id) + '">' + T.signIn + '</a> ' + T.roomsGateLink + '.</p>';
      } else {
        const cur = at && at.order === roomOrder ? (at.line.groupId || '') : null;
        const chip = (gid, label) => '<button type="button" class="psave__chip' + (cur === gid ? ' is-on' : '') + '" data-psave-room="' + esc(gid) + '" data-psave-oid="' + esc(roomOrder.id) + '" data-psave-nav="r:' + esc(roomOrder.id) + ':' + esc(gid) + '" aria-pressed="' + (cur === gid) + '">' + esc(label) + '</button>';
        html += '<div class="psave__rooms">' + chip('', T.noRoom) +
          store.groupsOf(roomOrder).map((g) => chip(g.id, g.label || (T.roomPh + store.nextRoomExample(roomOrder)))).join('') +
          (popRoomInput
            ? '<input type="text" class="psave__roomin" data-psave-roomin data-psave-oid="' + esc(roomOrder.id) + '" data-psave-nav="rin:' + esc(roomOrder.id) + '" placeholder="' + esc(T.roomPh + store.nextRoomExample(roomOrder)) + '" maxlength="120" aria-label="' + esc(T.roomLabel) + '" autocomplete="off">'
            : '<button type="button" class="psave__chip psave__chip--new" data-psave-newroom data-psave-oid="' + esc(roomOrder.id) + '" data-psave-nav="rn:' + esc(roomOrder.id) + '">' + T.newRoom + '</button>') +
          '</div>';
      }
      return html + '</div>';
    }
    function popoutRows(doc, query) {
      const q = (query || '').trim().toLowerCase();
      const open = store.activeProjects(doc);
      const matches = open.filter((p) => !q || p.name.toLowerCase().includes(q));
      let html = matches.map((p) => {
        const target = store.activeOrder(doc, p);
        // saved ANYWHERE in the project's open orders (it may sit in a
        // non-active order now that the popover can put it there)
        const at = store.findInProject(p, popData.handle);
        const inList = !!at;
        const pls = store.projectLines(p);
        const est = store.estimate(pls);
        const showOrder = store.openOrders(p).length > 1;
        const expanded = popOpenPid === p.id;
        const sub = at ? whereText(at) : (showOrder ? store.orderName(target) : '');
        return '<div class="psave__proj' + (expanded ? ' is-open' : '') + '">' +
          '<div class="psave__rowwrap">' +
          '<button type="button" class="psave__row" data-psave-project="' + p.id + '" data-psave-nav="p:' + esc(p.id) + '" title="' + (inList ? T.popRemove : T.popAdd) + '" aria-pressed="' + inList + '">' +
          '<span class="psave__check' + (inList ? ' is-on' : '') + '" aria-hidden="true"></span>' +
          '<span class="psave__name">' + esc(p.name) + (sub ? '<small class="psave__order">' + esc(sub) + '</small>' : '') + '</span>' +
          '<span class="psave__count">' + pls.length + (est ? ' · ' + money(est) : '') + '</span></button>' +
          '<button type="button" class="psave__more" data-psave-expand="' + esc(p.id) + '" data-psave-nav="x:' + esc(p.id) + '" aria-expanded="' + expanded + '" aria-controls="psave-lv-' + esc(p.id) + '" aria-label="' + esc(T.popChoose) + '" title="' + esc(T.popChoose) + '">' + CARET + '</button>' +
          '</div>' +
          (expanded ? popLevels(doc, p, at) : '') +
          '</div>';
      }).join('');
      const exact = open.some((p) => p.name.toLowerCase() === q);
      if (q && !exact) {
        html += '<button type="button" class="pcombo__create" data-psave-createmiss data-psave-nav="create" data-name="' + esc(query.trim()) + '">' +
          T.comboCreate + ' “<b>' + esc(query.trim()) + '</b>”</button>';
      }
      return html;
    }
    /** re-render the rows, keep the keyboard's place, keep the popover on screen */
    function paintPopRows(focusKey) {
      if (!pop) return;
      const si = pop.querySelector('[data-psave-search]');
      const rows = pop.querySelector('[data-psave-rows]');
      const active = document.activeElement;
      const key = focusKey || (active && pop.contains(active) && active.dataset ? active.dataset.psaveNav : null);
      if (rows) rows.innerHTML = popoutRows(store.read(), si ? si.value : '');
      if (key) {
        const el = pop.querySelector('[data-psave-nav="' + String(key).replace(/"/g, '\\"') + '"]');
        if (el) el.focus();
      }
      placePopout();
    }
    function placePopout() {
      if (!pop || !popTrigger) return;
      const r = popTrigger.getBoundingClientRect();
      const pw = Math.min(300, innerWidth - 16);
      pop.style.width = pw + 'px';
      const left = Math.min(Math.max(8, r.right - pw), innerWidth - pw - 8);
      let top = r.bottom + 8;
      const ph = pop.offsetHeight || 300;
      if (top + ph > innerHeight - 8) top = Math.max(8, Math.min(r.top - 8 - ph, innerHeight - 8 - ph));
      if (top < 8) top = 8;
      pop.style.left = left + 'px';
      pop.style.top = top + 'px';
    }
    function savePopout(btn, viaKeyboard) {
      closePopout();
      // hard gate (guestBuffer off): saving needs an account, go sign in
      if (!loggedIn && !guestBuffer) { gotoLogin(); return; }
      popData = { ...btn.dataset };
      popTrigger = btn;
      const doc = store.read();
      pop = document.createElement('div');
      pop.className = 'psave';
      pop.setAttribute('role', 'dialog');
      pop.setAttribute('aria-label', T.saveTo);
      pop.innerHTML = '<p class="psave__title">' + T.saveTo + '</p>' +
        (loggedIn ? '' : '<a class="psave__gate" href="' + esc(loginHref()) + '">' + AUTH_ICON + '<span>' + T.guestNotice + '</span></a>') +
        '<input type="text" class="psave__search" data-psave-search placeholder="' + T.popSearchPh + '" autocomplete="off" spellcheck="false" maxlength="60">' +
        '<div class="psave__rows" data-psave-rows>' + popoutRows(doc, '') + '</div>' +
        '<button type="button" class="psave__open" data-psave-open>' + T.openList + '</button>';
      document.body.appendChild(pop);
      placePopout();
      // a keyboard open lands on the first project row; a pointer open leaves
      // focus alone (focusing the search field would raise the touch keyboard)
      if (viaKeyboard) { const first = pop.querySelector('[data-psave-nav]'); if (first) first.focus(); }
    }
    document.addEventListener('input', (e) => {
      if (e.target.hasAttribute && e.target.hasAttribute('data-psave-search') && pop) paintPopRows();
      if (e.target.hasAttribute && e.target.hasAttribute('data-pline-qtyin')) commitTypedQty(e.target);
    });
    /** the typed quantity: '' is a draft (nothing happens until blur), a valid
     *  integer commits at once — 0 through the removal path — and the row is
     *  re-rendered with the caret kept at the end of the same field */
    function commitTypedQty(input) {
      const v = input.value;
      if (v === '') return;
      const n = Math.floor(Number(v));
      if (!Number.isFinite(n) || n < 0) return;
      const s = orderScope(input);
      const row = input.closest('[data-pline]');
      const i = Number(row.dataset.pline);
      const l = s.o.lines[i];
      if (!l || n === (l.qty || 1)) return;
      if (n === 0) { removeLineAnimated(input, s, i); return; }
      const lid = l.id; const inBoard = !!input.closest('[data-project-board]');
      store.setQty(s.o, i, n - (l.qty || 1));
      store.save(s.doc); paint(); renderAll();
      const scope = document.querySelector(inBoard ? '[data-project-board]' : '[data-project-root]');
      const again = scope && scope.querySelector('[data-pline-id="' + lid + '"] [data-pline-qtyin]');
      if (again) focusQtyAtEnd(again);
    }
    document.addEventListener('focusout', (e) => {
      // blur resets a draft ('' or an invalid value) to what is stored
      if (e.target.hasAttribute && e.target.hasAttribute('data-pline-qtyin')) {
        const s = orderScope(e.target); const row = e.target.closest('[data-pline]'); const l = row && s.o.lines[Number(row.dataset.pline)];
        if (l && String(l.qty || 1) !== e.target.value) e.target.value = l.qty || 1;
      }
    });
    document.addEventListener('keydown', (e) => {
      if (!pop || !e.target.closest || !e.target.closest('.psave')) return;
      const t = e.target;
      if (t.hasAttribute('data-psave-roomin')) {
        if (e.key === 'Enter') { e.preventDefault(); commitNewRoom(t); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); popRoomInput = false; paintPopRows('rn:' + t.dataset.psaveOid); }
        return;
      }
      if (t.hasAttribute('data-psave-search') && e.key === 'Enter') {
        e.preventDefault();
        const first = pop.querySelector('[data-psave-project],[data-psave-createmiss]');
        if (first) first.click();
        t.value = '';
        paintPopRows();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = [].slice.call(pop.querySelectorAll('[data-psave-search],[data-psave-nav],[data-psave-open]'));
        const i = items.indexOf(document.activeElement);
        const next = items[e.key === 'ArrowDown' ? Math.min(items.length - 1, i + 1) : Math.max(0, i - 1)];
        if (next) { e.preventDefault(); next.focus(); }
        return;
      }
      const projEl = t.closest('.psave__proj');
      const pid = projEl && projEl.querySelector('[data-psave-project]') ? projEl.querySelector('[data-psave-project]').dataset.psaveProject : null;
      if (e.key === 'ArrowRight' && pid && !t.hasAttribute('data-psave-search') && popOpenPid !== pid) {
        e.preventDefault(); popOpenPid = pid; popRoomInput = false; paintPopRows('x:' + pid);
      } else if (e.key === 'ArrowLeft' && pid && popOpenPid === pid && !t.hasAttribute('data-psave-search')) {
        e.preventDefault(); popOpenPid = null; popRoomInput = false; paintPopRows('p:' + pid);
      }
    });
    function savedAt(doc, pid) {
      const p = store.byId(doc, pid);
      return p ? store.findInProject(p, popData.handle) : null;
    }
    /** level 1, one tap: un-save from wherever it is, else save to the
     *  active order with no room — today's behaviour, unchanged */
    function addToProject(pid) {
      const doc = store.read();
      let added = false;
      if (savedAt(doc, pid)) store.removeFromProject(doc, pid, popData.handle);
      else added = store.toggleIn(doc, pid, popData);
      doc.activeProjectId = pid;
      store.save(doc); paint(); renderAll();
      if (added) showAdded(1);
      paintPopRows();
    }
    /** levels 2 and 3: save into a chosen order (+ room), or move there */
    function saveToOrder(pid, oid, gid) {
      const doc = store.read();
      const r = store.saveTo(doc, pid, oid, popData, gid);
      store.save(doc); paint(); renderAll();
      if (r.result === 'added') showAdded(1, whereText({ order: r.order, line: r.line }));
      return r;
    }
    function commitNewRoom(input) {
      const label = input.value.trim();
      const levels = input.closest('[data-psave-pid]');
      popRoomInput = false;
      if (!label || !levels) { paintPopRows('rn:' + input.dataset.psaveOid); return; }
      const pid = levels.dataset.psavePid; const oid = input.dataset.psaveOid;
      const doc = store.read();
      const p = store.byId(doc, pid); const o = p && store.orderById(p, oid);
      if (!o) { paintPopRows(); return; }
      const g = store.addGroup(o, label);
      const r = store.saveTo(doc, pid, oid, popData, g.id);
      store.save(doc); paint(); renderAll();
      if (r.result === 'added') showAdded(1, whereText({ order: r.order, line: r.line }));
      paintPopRows('r:' + oid + ':' + g.id);
    }

    /* ---------- kit add ---------- */
    /** cart attributes mapping a cart to one order — this is what flows onto
     *  the Shopify order for the merchant's PO -> invoice chain. Empty-string
     *  values remove previously set attributes. */
    function orderAttributes(p, o) {
      const ref = o ? store.orderRef(p, o) : '';
      const base = {
        'Project': p.name,
        'Project #': p.projectNumber || '',
        'PO #': o ? (o.poNumber || '') : '',
        'Quote #': o ? (o.quoteNumber || '') : '',
        'Order': o ? (store.orderName(o) + (ref ? ' (' + ref + ')' : '')) : '',
        '_st_order_id': o ? o.id : '',
      };
      return cfg.cartAttributes ? cfg.cartAttributes(p, o, base) : base;
    }
    async function pushItems(items, attrs, clearFirst) {
      if (clearFirst) await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
      if (attrs) {
        await fetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
        }).catch(() => {});
      }
      if (cfg.addToCart) {
        const handled = await cfg.addToCart(items);
        if (handled) return true;
      }
      const res = await fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!res.ok) return false;
      if (cfg.afterKitAdd) { cfg.afterKitAdd(items); return true; }
      const ev = new CustomEvent('project:kit-added', { detail: { items: items }, cancelable: true });
      document.dispatchEvent(ev);
      if (!ev.defaultPrevented) location.href = '/cart';
      return true;
    }
    /** LINE PROPERTIES: every detail of where a line came from travels ON
     *  THE LINE (Luke, 2026-09-17: "carry over all of the original details
     *  ... on the line level of the cart for each product (what room, what
     *  order, etc.) ... the customer has the ability to add more items to the
     *  cart that are not related to the project"). Cart attributes cannot do
     *  that — one cart, many orders — so the project, order and room ride as
     *  Shopify line item properties, which the cart drawer shows, checkout
     *  prints and the merchant sees on the order. Empty values are omitted
     *  (Shopify keeps them otherwise). The same variant from two rooms is two
     *  cart lines on purpose: the properties differ. Quantities are the
     *  list's quantities, untouched. */
    function lineProperties(p, o, l) {
      const props = {};
      const put = (k, v) => { if (v != null && String(v).trim() !== '') props[k] = String(v).trim(); };
      put('Project', p.name);
      put('Project #', p.projectNumber);
      /* hidden ids (underscore = not printed at checkout, not listed by the
       * cart's See details): the cart's context tag sends them back in
       * 'project:open' so the drawer can land on this exact order — the names
       * alone are ambiguous once two projects share a name or an order is
       * renamed (Luke, 2026-09-17: "the project name should be clickable to
       * click and open back up the project they came from"). */
      put('_project_id', p.id);
      if (o) put('_order_id', o.id);
      put('Customer / Site', p.customerRef);
      if (o) {
        const ref = store.orderRef(p, o);
        put('Order', store.orderName(o) + (ref ? ' (' + ref + ')' : ''));
        put('PO #', o.poNumber);
        put('Quote #', o.quoteNumber);
        put('Ready by', o.readyBy);
        const g = l.groupId ? store.groupsOf(o).find((x) => x.id === l.groupId) : null;
        if (g && g.label) put('Room', g.label);
      }
      put('Note', l.note);
      return cfg.lineProperties ? cfg.lineProperties(p, o, l, props) : props;
    }
    async function linesToItems(doc, p, o, ls) {
      await store.resolveVariants(ls);
      store.save(doc);
      return ls.filter((l) => l.variantId).map((l) => {
        const props = lineProperties(p, o, l);
        const item = { id: l.variantId, quantity: l.qty || 1 };
        if (Object.keys(props).length) item.properties = props;
        return item;
      });
    }
    /** every open order of the project, each line stamped with its own
     *  order and room; cart attributes carry only the project */
    async function addAllToCart(projectId) {
      const doc = store.read();
      const p = projectId ? store.byId(doc, projectId) : store.active(doc);
      if (!p) return;
      let items = [];
      for (const o of store.openOrders(p)) items = items.concat(await linesToItems(doc, p, o, o.lines));
      if (items.length) await pushItems(items, orderAttributes(p, null), false);
    }
    /** one order into the cart — APPENDED. The old replace-or-append prompt
     *  existed because a cart could carry only one order's PO in its
     *  attributes; with the PO on each line that constraint is gone and a
     *  cart may hold several orders plus unrelated items. */
    /* OPEN FROM THE CART (Luke, 2026-09-17: "the project name should be
     * clickable to click and open back up the project they came from to
     * allow the user to see some context on where it came from"). The cart
     * line's context tag dispatches 'project:open' with the hidden ids its
     * properties carry (_project_id / _order_id, lineProperties above) and
     * the visible names as a fallback for lines added before the ids
     * existed. The drawer switches to that project and order, opens, scrolls
     * the order's band into view and flags it .is-here for the theme's
     * highlight; an unknown project just opens the drawer. */
    document.addEventListener('project:open', (e) => {
      const d = (e && e.detail) || {};
      const doc = store.read();
      const p = (d.projectId && store.byId(doc, d.projectId)) || (d.project && doc.projects.find((x) => x.name === d.project && x.status !== 'archived')) || null;
      if (!p) { openDrawer(); return; }
      const wantName = String(d.order || '').replace(/\s*\(.*\)$/, '');
      const o = (d.orderId && store.orderById(p, d.orderId)) || (wantName && p.orders.find((x) => store.orderName(x) === wantName)) || null;
      doc.activeProjectId = p.id;
      if (o) p.activeOrderId = o.id;
      store.save(doc); paint(); renderAll();
      openDrawer();
      if (!o) return;
      requestAnimationFrame(() => {
        const sec = document.querySelector('[data-project-root] [data-porder-sec="' + o.id + '"]');
        if (!sec) return;
        sec.scrollIntoView({ block: 'start', behavior: REDUCED ? 'auto' : 'smooth' });
        sec.classList.add('is-here');
        setTimeout(() => sec.classList.remove('is-here'), 1600);
      });
    });
    async function addOrderToCart(projectId, orderId) {
      const doc = store.read();
      const p = store.byId(doc, projectId) || store.active(doc);
      const o = store.orderById(p, orderId);
      if (!o || !o.lines.length) return;
      const items = await linesToItems(doc, p, o, o.lines);
      if (!items.length) return;
      await pushItems(items, orderAttributes(p, null), false);
    }
    function orderScope(el) {
      const doc = store.read();
      const scope = el.closest('[data-pproj]');
      const p = (scope ? store.byId(doc, scope.dataset.pproj) : null) || store.active(doc);
      const ow = el.closest('[data-porder]');
      const o = (ow ? store.orderById(p, ow.dataset.porder) : null) || store.activeOrder(doc, p);
      return { doc: doc, p: p, o: o };
    }
    /* room drag + drop: a line drops onto a room's line list (or Unassigned);
       a room header drops onto another room's section to take its place */
    let drag = null;
    const dropZoneFor = (el) => el.closest && (drag && drag.type === 'group' ? el.closest('[data-pgroup]') : el.closest('[data-pgroup-drop]'));
    document.addEventListener('dragstart', (e) => {
      // room headers only: lines moved to the pointer-driven drag above
      const gh = e.target.closest && e.target.closest('[data-pgroup-drag]');
      if (!gh) return;
      const ln = null;
      drag = { type: 'group', id: gh.dataset.pgroupDrag };
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', drag.id); } catch (err) { /* older engines */ }
      (gh || ln).classList.add('is-dragging');
    });
    document.addEventListener('dragover', (e) => {
      if (!drag) return;
      const zone = dropZoneFor(e.target);
      if (!zone) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!zone.classList.contains('is-over')) {
        document.querySelectorAll('.is-over').forEach((x) => x.classList.remove('is-over'));
        zone.classList.add('is-over');
      }
    });
    document.addEventListener('drop', (e) => {
      if (!drag) return;
      const zone = dropZoneFor(e.target);
      if (!zone) return;
      e.preventDefault();
      const s = orderScope(zone);
      if (drag.type === 'line') store.moveLine(s.o, drag.id, zone.dataset.pgroupDrop || null);
      else { const idx = Number(zone.dataset.pgroupIndex); if (idx >= 0) store.moveGroup(s.o, drag.id, idx); }
      drag = null;
      store.save(s.doc); paint(); renderAll();
    });
    document.addEventListener('dragend', () => {
      drag = null;
      document.querySelectorAll('.is-dragging,.is-over').forEach((x) => x.classList.remove('is-dragging', 'is-over'));
    });
    document.addEventListener('click', (e) => {
      const saveBtn = e.target.closest('[data-wishlist],[data-add-project]');
      if (saveBtn) {
        e.preventDefault();
        if (saveBtn.hasAttribute('data-add-project') && !saveBtn.dataset.variant) {
          const vin = document.querySelector('[data-product-form] [data-variant-id], form[action*="/cart/add"] [name="id"]');
          if (vin && vin.value) saveBtn.dataset.variant = vin.value;
        }
        if (pop && popData && popData.handle === saveBtn.dataset.handle) closePopout();
        else savePopout(saveBtn, e.detail === 0); // detail 0 = keyboard activation
        return;
      }
      /* save popover, levels 2 and 3 */
      const pexp = e.target.closest('[data-psave-expand]');
      if (pexp) {
        popOpenPid = popOpenPid === pexp.dataset.psaveExpand ? null : pexp.dataset.psaveExpand;
        popRoomInput = false;
        paintPopRows('x:' + pexp.dataset.psaveExpand);
        return;
      }
      const pord = e.target.closest('[data-psave-order]');
      if (pord) {
        saveToOrder(pord.closest('[data-psave-pid]').dataset.psavePid, pord.dataset.psaveOrder);
        paintPopRows('o:' + pord.dataset.psaveOrder);
        return;
      }
      const pnew = e.target.closest('[data-psave-neworder]');
      if (pnew) {
        const pid = pnew.closest('[data-psave-pid]').dataset.psavePid;
        const doc1 = store.read();
        const o1 = store.addOrder(doc1, pid);
        store.save(doc1);
        saveToOrder(pid, o1.id);
        paintPopRows('o:' + o1.id);
        return;
      }
      const proom = e.target.closest('[data-psave-room]');
      if (proom) {
        saveToOrder(proom.closest('[data-psave-pid]').dataset.psavePid, proom.dataset.psaveOid, proom.dataset.psaveRoom || null);
        paintPopRows(proom.dataset.psaveNav);
        return;
      }
      const prnew = e.target.closest('[data-psave-newroom]');
      if (prnew) {
        if (!requireLogin()) return; // rooms need an account (RFC §4)
        popRoomInput = true;
        paintPopRows('rin:' + prnew.dataset.psaveOid);
        return;
      }
      const cedit = e.target.closest('[data-pcombo-edit]');
      if (cedit) {
        const rowEl = cedit.closest('[data-pcombo-row]');
        const doc0 = store.read();
        const proj = store.byId(doc0, cedit.dataset.pcomboEdit);
        if (rowEl && proj) {
          rowEl.innerHTML = '<input class="pcombo__rename" data-pcombo-rename="' + proj.id + '" value="' + esc(proj.name) + '" maxlength="60">';
          const ri = rowEl.querySelector('input');
          ri.focus(); ri.select();
        }
        return;
      }
      const cdel = e.target.closest('[data-pcombo-del]');
      if (cdel) { openConfirm(cdel.dataset.pcomboDel); return; }
      const copt = e.target.closest('[data-pcombo-opt]');
      if (copt) { const c = comboEls(copt); comboCommit(c ? c.kind : 'project', copt.dataset.pcomboOpt, false); return; }
      const ccreate = e.target.closest('[data-pcombo-create]');
      if (ccreate) { const c = comboEls(ccreate); comboCommit(c ? c.kind : 'project', ccreate.dataset.name, true); return; }
      const row = e.target.closest('[data-psave-project]');
      if (row) { addToProject(row.dataset.psaveProject); return; }
      const cmiss = e.target.closest('[data-psave-createmiss]');
      if (cmiss) {
        const doc0 = store.read();
        const np = store.addProject(doc0, cmiss.dataset.name);
        store.save(doc0);
        addToProject(np.id);
        const si = pop && pop.querySelector('[data-psave-search]');
        if (si) { si.value = ''; }
        const rows0 = pop && pop.querySelector('[data-psave-rows]');
        if (rows0) rows0.innerHTML = popoutRows(store.read(), '');
        return;
      }
      if (e.target.closest('[data-psave-open]')) { closePopout(); openDrawer(); return; }
      if (e.target.closest('[data-open-project]')) { e.preventDefault(); openDrawer(); return; }
      if (e.target.closest('[data-project-new]')) {
        if (!requireLogin()) return; // named projects need an account (RFC §4)
        const doc = store.read();
        store.addProject(doc, 'Project ' + (doc.projects.length + 1));
        store.save(doc); paint(); renderAll();
        return;
      }
      const del = e.target.closest('[data-project-del]');
      if (del) {
        const scope = del.closest('[data-pproj]');
        openConfirm(scope ? scope.dataset.pproj : store.active(store.read()).id);
        return;
      }
      /* ---- rooms ---- */
      const gnew = e.target.closest('[data-pgroup-new]');
      if (gnew) {
        if (!requireLogin()) return; // rooms need an account (RFC §4)
        const s = orderScope(gnew);
        const o = store.orderById(s.p, gnew.dataset.pgroupNew) || s.o;
        const g = store.addGroup(o, '');
        store.save(s.doc); paint(); renderAll(); focusGroupLabel(g.id);
        return;
      }
      const gtog = e.target.closest('[data-pgroup-toggle]');
      if (gtog) {
        const s = orderScope(gtog);
        store.toggleGroup(s.o, gtog.dataset.pgroupToggle);
        store.save(s.doc); renderAll();
        return;
      }
      const gdel = e.target.closest('[data-pgroup-del]');
      if (gdel) {
        // no confirm: nothing is lost — the room's items fall back to Unassigned
        const s = orderScope(gdel);
        store.removeGroup(s.o, gdel.dataset.pgroupDel);
        store.save(s.doc); paint(); renderAll();
        return;
      }
      const qty = e.target.closest('[data-pline-qty]');
      if (qty) {
        const s = orderScope(qty);
        const qi = Number(qty.closest('[data-pline]').dataset.pline);
        const ql = s.o.lines[qi];
        // stepping to 0 removes the line: same tint-slide-collapse + Undo as the X
        if (ql && (ql.qty || 1) + Number(qty.dataset.plineQty) <= 0) { removeLineAnimated(qty, s, qi); return; }
        store.setQty(s.o, qi, Number(qty.dataset.plineQty));
        store.save(s.doc); paint(); renderAll();
        return;
      }
      const rm = e.target.closest('[data-pline-remove]');
      if (rm) {
        const s = orderScope(rm);
        removeLineAnimated(rm, s, Number(rm.closest('[data-pline]').dataset.pline));
        return;
      }
      const roomBadge = e.target.closest('[data-pline-room]');
      if (roomBadge) { openRoomPicker(roomBadge); return; }
      const noteBtn = e.target.closest('[data-pline-note-btn]');
      if (noteBtn) {
        const s = orderScope(noteBtn);
        const row = noteBtn.closest('[data-pline]');
        const l = s.o.lines[Number(row.dataset.pline)];
        if (!l) return;
        const nm = global.SydneyTaps && global.SydneyTaps.noteModal;
        if (nm && nm.open) {
          const pid = s.p.id; const oid = s.o.id; const lid = l.id;
          const inBoard = !!noteBtn.closest('[data-project-board]');
          nm.open({
            title: T.noteTitle, subtitle: l.title, value: l.note || '', maxLength: 500, placeholder: T.notePh, trigger: noteBtn,
            onSave: (text) => {
              const doc = store.read();
              const p = store.byId(doc, pid); const o = p && store.orderById(p, oid);
              const line = o && store.lineById(o, lid);
              if (!line) return;
              line.note = String(text || '').trim().slice(0, 500);
              store.save(doc); renderAll();
              // the trigger was re-rendered away: hand focus to its successor
              setTimeout(() => {
                const scope = document.querySelector(inBoard ? '[data-project-board]' : '[data-project-root]');
                const again = scope && scope.querySelector('[data-pline-id="' + lid + '"] [data-pline-note-btn]');
                if (again) again.focus();
              }, 60);
            },
          });
        } else {
          // no shared editor on this page: the old inline field, in place
          const input = document.createElement('input');
          input.type = 'text'; input.className = 'pline__note'; input.setAttribute('data-pline-note', '');
          input.value = l.note || ''; input.placeholder = T.notePh; input.maxLength = 500; input.setAttribute('aria-label', T.noteLabel);
          noteBtn.replaceWith(input); input.focus();
        }
        return;
      }
      const undoBtn = e.target.closest('[data-pundo-btn]');
      if (undoBtn) { undoRemove(undoBtn.dataset.pundoBtn); return; }
      const addall = e.target.closest('[data-project-addall]');
      if (addall) {
        const scope = addall.closest('[data-pproj]');
        addAllToCart(scope ? scope.dataset.pproj : null);
        return;
      }
      /* orders: activate (accordion switch), create, archive */
      const oact = e.target.closest('[data-porder-activate]');
      if (oact) {
        const doc = store.read();
        store.setActiveOrder(doc, store.active(doc).id, oact.dataset.porderActivate);
        store.save(doc); paint(); renderAll();
        return;
      }
      if (e.target.closest('[data-porder-new]')) {
        const doc = store.read();
        store.addOrder(doc, store.active(doc).id);
        store.save(doc); paint(); renderAll();
        return;
      }
      const odel = e.target.closest('[data-porder-del]');
      if (odel) {
        // on the board the order belongs to the card's project, not necessarily the active one
        const scope = odel.closest('[data-pproj]');
        openConfirm(scope ? scope.dataset.pproj : store.active(store.read()).id, odel.dataset.porderDel);
        return;
      }
      const chatBtn = e.target.closest('[data-pchat]');
      if (chatBtn) {
        const c = document.querySelector('shopify-chat');
        if (!c) return;
        if (c.hasAttribute('open')) { if (typeof c.close === 'function') c.close(); else c.removeAttribute('open'); }
        else if (typeof c.show === 'function') c.show(); else c.setAttribute('open', '');
        return;
      }
      const shareBtn = e.target.closest('[data-pshare]');
      if (shareBtn) {
        const projectId = store.active(store.read()).id;
        /* The event stays, and stays cancellable, so a host that wants its
           own share experience (a native share sheet on mobile, say) can take
           over. Nothing listened to it before this change — the button never
           rendered, because cfg.shareBase was never set. */
        const ev = new CustomEvent('project:share', { detail: { projectId: projectId }, cancelable: true });
        if (document.dispatchEvent(ev)) openSharePanel(projectId, shareBtn);
        return;
      }
      const shareAct = e.target.closest('[data-pshare-act]');
      if (shareAct) { shareAction(shareAct.dataset.pshareAct); return; }
      const oadd = e.target.closest('[data-porder-add]');
      if (oadd) {
        const doc0 = store.read();
        const scope = oadd.closest('[data-pproj]');
        addOrderToCart(scope ? scope.dataset.pproj : store.active(doc0).id, oadd.dataset.porderAdd);
        return;
      }
    });
    document.addEventListener('pointerdown', (e) => {
      if (e.target.closest && e.target.closest('.pconfirm')) return;
      if (pop && !e.target.closest('.psave') && !e.target.closest('[data-wishlist],[data-add-project]')) closePopout();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePopout(true); });
    addEventListener('scroll', closePopout, { passive: true });
    addEventListener('resize', closePopout);
    document.addEventListener('keydown', (e) => {
      if (e.target.hasAttribute && e.target.hasAttribute('data-pgroup-label') && e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
    });
    document.addEventListener('change', (e) => {
      const glabel = e.target.closest('[data-pgroup-label]');
      if (glabel) {
        const s = orderScope(glabel);
        store.renameGroup(s.o, glabel.dataset.pgroupLabel, glabel.value);
        store.save(s.doc);
        renderAll(); // change fires on blur, so re-rendering never steals focus
        return;
      }
      const noteEl = e.target.closest('[data-pline-note]');
      if (noteEl) {
        const s = orderScope(noteEl);
        const l = s.o.lines[Number(noteEl.closest('[data-pline]').dataset.pline)];
        if (l) { l.note = noteEl.value.trim().slice(0, 500); store.save(s.doc); renderAll(); }
        return; // change fires on blur: the re-render puts the note button back
      }
      const meta = e.target.closest('[data-pmeta]');
      if (meta) {
        const doc = store.read();
        const scope = meta.closest('[data-pproj]');
        const p = scope ? store.byId(doc, scope.dataset.pproj) : store.active(doc);
        if (p) {
          p[meta.dataset.pmeta] = meta.value.trim();
          store.save(doc);
          renderAll(); // change fires on blur, so re-rendering never steals focus
        }
        return;
      }
      const ometa = e.target.closest('[data-pometa]');
      if (ometa) {
        const doc = store.read();
        const scope = ometa.closest('[data-pproj]');
        const p = (scope ? store.byId(doc, scope.dataset.pproj) : null) || store.active(doc);
        const sec = ometa.closest('[data-porder-sec]');
        const o = sec ? store.orderById(p, sec.dataset.porderSec) : store.activeOrder(doc, p);
        if (o) {
          o[ometa.dataset.pometa] = ometa.value.trim();
          store.save(doc);
          renderAll();
        }
      }
    });

    /* ---------- share: the owner's link toggle ----------
       One `scope=link` share per project, minted by the projects app. The
       token is returned ONCE, because only its sha256 is stored — so the
       panel says so plainly rather than pretending the link can be fetched
       back later. Turning it off revokes it; turning it on again mints a NEW
       token, which is what makes "turn it off" mean something.

       The panel is built here, not in the theme, for the same reason the list
       has one renderer: a second copy in Liquid would drift. The theme owns
       only the skin (.pshare-* classes). */
    let sharePanel = null;
    function shareApi(path, init) {
      return fetch((cfg.shareBase || '') + path, Object.assign({ credentials: 'same-origin' }, init || {}));
    }
    function sharePanelHTML(state) {
      const on = !!state.on;
      const link = state.url || '';
      return '<div class="pshare__panel" role="dialog" aria-label="' + esc(T.shareTitle) + '">' +
        '<div class="pshare__head"><b>' + esc(T.shareTitle) + '</b>' +
        '<button type="button" class="pshare__x" data-pshare-act="close" aria-label="' + esc(T.shareClose) + '">×</button></div>' +
        '<p class="pshare__state">' + esc(on ? T.shareOn : T.shareOff) + '</p>' +
        (link
          ? '<div class="pshare__link"><input type="text" readonly value="' + esc(link) + '" data-pshare-url aria-label="' + esc(T.shareTitle) + '">' +
            '<button type="button" class="btn btn--ghost" data-pshare-act="copy">' + esc(T.shareCopy) + '</button></div>' +
            '<p class="pshare__note">' + esc(T.shareOnce) + '</p>'
          : (on ? '<p class="pshare__note">' + esc(T.shareLive) + '</p>' : '')) +
        '<div class="pshare__ctas">' +
        (on
          ? '<button type="button" class="btn btn--ghost" data-pshare-act="off">' + esc(T.shareDisable) + '</button>'
          : '<button type="button" class="btn" data-pshare-act="on">' + esc(T.shareEnable) + '</button>') +
        '</div></div>';
    }
    function paintSharePanel(state) {
      if (!sharePanel) return;
      sharePanel.el.innerHTML = sharePanelHTML(state);
      sharePanel.state = state;
      const input = sharePanel.el.querySelector('[data-pshare-url]');
      if (input) { input.focus(); input.select(); }
    }
    function closeSharePanel() {
      if (!sharePanel) return;
      sharePanel.el.remove();
      sharePanel = null;
    }
    async function openSharePanel(projectId, anchor) {
      closeSharePanel();
      const host = (anchor && anchor.closest('.flyout__foot')) || document.querySelector('[data-project-root]') || document.body;
      const el = document.createElement('div');
      el.className = 'pshare';
      el.innerHTML = '<div class="pshare__panel"><p class="pshare__state">' + esc(T.shareBusy) + '</p></div>';
      host.appendChild(el);
      sharePanel = { el: el, projectId: projectId, state: { on: false } };
      try {
        const res = await shareApi('/share?projectId=' + encodeURIComponent(projectId));
        if (!res.ok) throw new Error(String(res.status));
        paintSharePanel(await res.json());
      } catch (e) {
        if (sharePanel) sharePanel.el.innerHTML = '<div class="pshare__panel"><p class="pshare__state">' + esc(T.shareFailed) + '</p></div>';
      }
    }
    async function shareAction(action) {
      if (!sharePanel) return;
      if (action === 'close') return closeSharePanel();
      if (action === 'copy') {
        const input = sharePanel.el.querySelector('[data-pshare-url]');
        if (!input) return;
        input.select();
        const done = () => {
          const btn = sharePanel && sharePanel.el.querySelector('[data-pshare-act="copy"]');
          if (btn) btn.textContent = T.shareCopied;
        };
        /* navigator.clipboard is unavailable on insecure origins and in some
           in-app browsers; the selection above is the fallback, so a shopper
           can always copy by hand. */
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(input.value).then(done, () => {});
        } else { done(); }
        return;
      }
      const on = action === 'on';
      const projectId = sharePanel.projectId;
      sharePanel.el.querySelectorAll('button').forEach((b) => { b.disabled = true; });
      try {
        const res = await shareApi('/share', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: projectId, on: on }),
        });
        if (!res.ok) throw new Error(String(res.status));
        paintSharePanel(await res.json());
      } catch (e) {
        if (sharePanel) sharePanel.el.innerHTML = '<div class="pshare__panel"><p class="pshare__state">' + esc(T.shareFailed) + '</p></div>';
      }
    }

    /* ---------- share: the recipient's read-only view ----------
       The projects app serves /apps/projects/s/<token> as Liquid (so it wears
       the theme's own layout) carrying the document as JSON and an empty,
       `inert` mount. The list is painted HERE by listHTML — the SAME renderer
       as the drawer and the project page — because a second, Liquid copy of
       the list would drift from the drawer the first time either changed.

       The mount is inert so that none of the ~30 delegated handlers in this
       file can fire inside it: they all mutate the VIEWER's own document, and
       on somebody else's list that would be silent corruption. The two things
       a viewer MAY do live outside the inert subtree. */
    function renderSharedView() {
      const payloadEl = document.querySelector('[data-pshared-payload]');
      const mount = document.querySelector('[data-project-shared]');
      if (!payloadEl || !mount) return;
      let payload = null;
      try { payload = JSON.parse(payloadEl.textContent || 'null'); } catch (e) { return; }
      if (!payload || !payload.doc) return;
      const doc = core.migrate(payload.doc);
      if (!doc) return;
      const built = listHTML(doc, true);
      mount.innerHTML = built.html;
      /* `inert` is not in every browser we support; aria-hidden + a pointer
         guard is the belt to its braces, and the handlers below ignore
         anything inside the mount anyway. */
      mount.setAttribute('inert', '');
      document.addEventListener('click', async (e) => {
        if (e.target.closest('[data-pshared-addall]')) {
          /* NOT linesToItems(): that one calls store.save(doc) to persist the
             variant ids it resolved, which on this page would write somebody
             else's list into the viewer's own localStorage. Resolve, build,
             and keep the shared document out of local storage entirely.
             No cart attributes either — the viewer's cart must not be tagged
             with the sharer's project id and PO. */
          if (e.target.closest('[data-pshared-addall]').disabled) return;
          const p = store.active(doc);
          const ls = store.projectLines(p);
          await store.resolveVariants(ls);
          const items = ls.filter((l) => l.variantId).map((l) => ({ id: l.variantId, quantity: l.qty || 1 }));
          if (items.length) await pushItems(items, null, false);
          return;
        }
        const save = e.target.closest('[data-pshared-save]');
        if (save) {
          save.disabled = true;
          try {
            const res = await fetch('/apps/projects/s/' + encodeURIComponent(payload.token) + '/accept', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' }, body: '{}',
            });
            save.textContent = res.ok ? 'Saved to your account' : 'Could not save it';
          } catch (err) { save.textContent = 'Could not save it'; }
        }
      });
    }

    // async surfaces (search islands etc.) — repaint save states as they appear
    const mo = new MutationObserver(() => {
      clearTimeout(ProjectListUI._t);
      ProjectListUI._t = setTimeout(paint, 150);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    paint(); render(); renderBoard(); renderSharedView();
    // signed in + proxy configured: merge the buffer on the first boot on
    // this device, pull the account doc after; every failure leaves the
    // shopper on the buffer (ProxyAdapter in project-core.js)
    if (adapter && adapter.boot) adapter.boot();
    /* Opening the drawer is the moment a shopper expects to see what they
       saved on the other device, and the poll interval may have just fired
       against a closed surface. One immediate conditional fetch on open costs
       a 304 when nothing changed. */
    document.addEventListener('flyout:open', (e) => {
      if (e.detail !== 'project') return;
      if (adapter && adapter.refresh) adapter.refresh();
    });
    return { paint: paint, render: renderAll, store: store, adapter: adapter, addAllToCart: addAllToCart, addOrderToCart: addOrderToCart };
  }

  global.ProjectListUI = ProjectListUI;
  if (!global.PROJECT_MANUAL_INIT) {
    // boot once no matter how many times the script is included — a second
    // instance duplicates every document-level handler (double creates,
    // double archives, double qty steps)
    const boot = () => {
      if (global.__projectListBooted) return;
      global.__projectListBooted = true;
      ProjectListUI(global.PROJECT_CONFIG || {});
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }
})(window);
